import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Download, Trash2, LogOut, Users, Clock, Building2, Mail, Phone, ChevronDown, ExternalLink, Tag, MapPin, Calendar, Filter } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Blocked personal/free email domains — single source of truth
const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.in', 'yahoo.co.in', 'ymail.com',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'rediffmail.com',
  'icloud.com', 'me.com', 'proton.me', 'protonmail.com', 'aol.com',
  'gmx.com', 'zoho.com', 'mail.com', 'rocketmail.com', 'tutanota.com', 'fastmail.com'
];

const isOfficialEmail = (email) => {
  const domain = email.split('@')[1]?.toLowerCase().trim();
  return domain && !BLOCKED_EMAIL_DOMAINS.includes(domain);
};

// Error Boundary — shows fallback UI instead of blank page on crash
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("Campaign page error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#1e3a8a 0%,#2563EB 50%,#3b82f6 100%)",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"16px",padding:"48px 40px",maxWidth:"480px",width:"100%",textAlign:"center",boxShadow:"0 20px 40px rgba(0,0,0,0.15)"}}>
            <div style={{width:"56px",height:"56px",borderRadius:"50%",background:"#FEE2E2",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:"24px",color:"#DC2626"}}>!</div>
            <h2 style={{margin:"0 0 12px",fontSize:"22px",color:"#171717",fontWeight:"600"}}>Something went wrong</h2>
            <p style={{margin:"0 0 24px",fontSize:"15px",color:"#525252",lineHeight:"1.6"}}>We're experiencing a temporary issue. Please try refreshing the page or contact us for assistance.</p>
            <a href="mailto:mail@locofast.com" style={{display:"inline-block",background:"#2563EB",color:"white",padding:"12px 28px",borderRadius:"8px",textDecoration:"none",fontWeight:"600",fontSize:"15px",marginBottom:"12px"}}>mail@locofast.com</a>
            <br/>
            <button onClick={() => window.location.reload()} style={{background:"none",border:"1px solid #d4d4d4",padding:"10px 24px",borderRadius:"8px",cursor:"pointer",fontSize:"14px",color:"#525252",marginTop:"8px",fontFamily:"inherit"}}>Refresh Page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const BRAND_TICKER = ["Myntra Private Labels", "D2C Brands", "Flipkart Brands", "Meesho Sellers", "Reliance Manufacturers", "Westside Manufacturers", "Ajio Brand Partners", "Amazon Sellers", "Nykaa Fashion Labels", "Export Houses", "Bangladesh Top Factories", "Shoppers Stop Vendors"];

const GLOBAL_BRAND_TICKER = ["Walmart Manufacturers", "H&M Manufacturers", "Target Manufacturers", "Aldi Manufacturers", "Amazon Private Labels", "Primark Manufacturers", "Levi's Manufacturers", "Zara Manufacturers", "Gap Manufacturers", "C&A Manufacturers", "Uniqlo Manufacturers", "Nike Manufacturers"];


// Bangladesh Landing Page Component (Campaign 1)
const BangladeshLandingPage = () => {
  const [formData, setFormData] = useState({
    company: "",
    email: "",
    companyType: "",
    phone: "",
    binNumber: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Fire Lead pixel on thank you page
  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const factories = [
    "Ananta Group", "Goldstar Group", "Snowtex", "Hameem Group", "Windy Group",
    "Sharmin Group", "Spider Group", "Ram Group", "Valmont Group", "Florence Group",
    "Unifill Group", "Shinest Group", "A Plus Group", "Experience Textiles", "Amtranet Group"
  ];

  const validateBIN = (bin) => /^\d{13}$/.test(bin);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid work email.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Please use your official company email (not Gmail, Yahoo, etc.)";
    }
    if (!formData.companyType) newErrors.companyType = "Please select your company type.";
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number.";
    if (!formData.binNumber.trim()) {
      newErrors.binNumber = "Please enter your BIN number.";
    } else if (!validateBIN(formData.binNumber)) {
      newErrors.binNumber = "BIN must be exactly 13 digits. Example: 0012345678901";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        company: formData.company,
        email: formData.email,
        company_type: formData.companyType,
        phone: formData.phone ? `+880${formData.phone}` : null,
        bin_number: formData.binNumber,
        campaign: "bangladesh"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <div className="fold">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <svg viewBox="0 0 16 16" fill="none"><path d="M8 1l1.545 3.09L13 4.636l-2.5 2.436.59 3.428L8 8.91l-3.09 1.59.59-3.428L3 4.636l3.455-.546L8 1z" fill="white" opacity="0.9"/></svg>
            <span>Trusted by 500+ factories & buying houses in Bangladesh</span>
          </div>
          <h1>Buy Fabrics from<br/>India's Largest<br/>B2B Textile Platform</h1>
          <p className="hero-sub">Connect directly with 500+ verified seller partners.<br/>Transparent pricing, free samples & secure payments.</p>
          <div className="feat-strip">
            <div className="feat-item">
              <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="white" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>500+ Seller Partners</span>
            </div>
            <div className="feat-item">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 2l1.2 3.6H13l-3.1 2.2 1.2 3.6L8 9.2l-3.1 2.2 1.2-3.6L3 5.6h3.8L8 2z" fill="white" opacity="0.8"/></svg>
              <span>Verified Suppliers</span>
            </div>
            <div className="feat-item">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 2C5.238 2 3 4.238 3 7c0 3.75 5 9 5 9s5-5.25 5-9c0-2.762-2.238-5-5-5z" stroke="white" strokeWidth="1.5"/><circle cx="8" cy="7" r="1.5" fill="white"/></svg>
              <span>Money Safety Guarantee</span>
            </div>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Sourced by leading Bangladesh factories</div>
          <div className="marquee-track">
            {[...factories, ...factories].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-warning-banner" data-testid="spam-warning">
              <span className="warning-icon">&#9888;</span>
              <div>
                <div className="warning-bn">শুধুমাত্র লাইসেন্সধারী আমদানিকারক ও গার্মেন্ট প্রস্তুতকারকদের জন্য</div>
                <div className="warning-en">Only for licensed importers & garment manufacturers. Applications without valid BIN / trade licence will not be processed.</div>
              </div>
            </div>

            <div className="form-head">আজই শুরু করুন <span className="form-head-en">Get Started Today</span></div>
            <div className="form-sub">আমাদের বাংলাদেশ টিম ২৪ ঘণ্টার মধ্যে যোগাযোগ করবে। <span className="form-sub-en">Our Bangladesh team will reach out within 24 hours.</span></div>

            <div className="form-notice" data-testid="sample-notice">
              <div className="notice-bn">বৈধ BIN নম্বর সহ শুধুমাত্র নিবন্ধিত কোম্পানিগুলো (প্রস্তুতকারক) বিনামূল্যে নমুনা পাবে।</div>
              <div className="notice-en">Only registered companies (manufacturers) with valid BIN numbers will receive free samples.</div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="company">কোম্পানির নাম <span className="label-en">Company Name</span> <span className="req">*</span></label>
                <input type="text" id="company" data-testid="company-input" placeholder="যেমন: ABC Garments Ltd."
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                {errors.company && <div className="field-error">{errors.company}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">অফিসিয়াল ইমেইল <span className="label-en">Official Email</span> <span className="req">*</span></label>
                <input type="email" id="email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
                {!errors.email && <div className="field-hint">Gmail, Yahoo ইত্যাদি গ্রহণযোগ্য নয় · No personal emails</div>}
              </div>

              <div className={`field ${errors.companyType ? "error" : ""}`}>
                <label htmlFor="companyType">কোম্পানির ধরন <span className="label-en">Company Type</span> <span className="req">*</span></label>
                <div className="select-wrapper">
                  <select id="companyType" data-testid="company-type-select" value={formData.companyType}
                    onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}>
                    <option value="" disabled>আপনার ধরন নির্বাচন করুন · Select your type</option>
                    <option value="garment_factory">গার্মেন্ট ফ্যাক্টরি · Garment Factory</option>
                    <option value="buying_house">বায়িং হাউস · Buying House</option>
                    <option value="others">অন্যান্য · Others</option>
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                {errors.companyType && <div className="field-error">{errors.companyType}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="phone">ফোন নম্বর <span className="label-en">Phone Number</span> <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+880</div>
                  <input type="tel" id="phone" data-testid="phone-input" placeholder="1XXXXXXXXX" maxLength="11"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.binNumber ? "error" : ""}`}>
                <label htmlFor="binNumber">BIN নম্বর <span className="label-en">BIN Number</span> <span className="req">*</span></label>
                <input type="text" id="binNumber" data-testid="bin-input" placeholder="0012345678901" maxLength="13"
                  value={formData.binNumber} onChange={(e) => setFormData({ ...formData, binNumber: e.target.value.replace(/\D/g, "") })} />
                {errors.binNumber && <div className="field-error">{errors.binNumber}</div>}
                {!errors.binNumber && <div className="field-hint">১৩ সংখ্যার ব্যবসায়িক সনাক্তকরণ নম্বর · 13-digit Business Identification Number</div>}
              </div>

              <button type="submit" className="submit-btn" data-testid="submit-btn" disabled={isSubmitting}>
                <span className="btn-text">{isSubmitting ? "জমা হচ্ছে..." : "Locofast-এ যোগ দিন · Join Locofast"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                জমা দিয়ে আপনি আমাদের <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">শর্তাবলী</a>তে সম্মত হচ্ছেন। আপনার তথ্য কখনও শেয়ার করা হয় না।
                <br/><span className="form-note-en">By submitting you agree to our Terms of Service. Your data is never shared.</span>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon">✓</div>
            <div className="success-title">আপনি তালিকায় আছেন! <span className="form-head-en">You're on the list!</span></div>
            <div className="success-sub">আমাদের বাংলাদেশ টিম ২৪ ঘণ্টার মধ্যে যোগাযোগ করবে। <br/><span className="form-sub-en">Our Bangladesh team will reach out within 24 hours. Explore our full catalogue at shop.locofast.com</span></div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn">ফেব্রিক দেখুন · Explore Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">700+</div><div className="stat-lbl">Active<br/>Buyers</div></div>
          <div className="stat"><div className="stat-val">5,000+</div><div className="stat-lbl">Orders<br/>Placed</div></div>
        </div>

        <section className="section">
          <div className="section-label">Why Locofast</div>
          <div className="section-title">Everything in<br/>one platform.</div>
          <div className="vp-list">
            {[
              { title: "Compare Mill Rates Directly", desc: "Competing quotes from 500+ verified Indian mills on one screen — no agents, no inflated pricing." },
              { title: "Unlimited Free Samples", desc: "Request samples from any mill at zero cost with real-time dispatch tracking." },
              { title: "End-to-End Logistics View", desc: "Live shipment tracking from mill gate to your factory door, every step." },
              { title: "Dedicated Seller Manager", desc: "A textile specialist negotiates rates on your behalf and finds the perfect match." },
              { title: "Unsecured Trade Credit", desc: "Buy now, pay later via Locofast's lending partners — no collateral needed." },
              { title: "Payment Protection", desc: "Funds held by Locofast and released only after you confirm quality on delivery." }
            ].map((vp, i) => (
              <div key={i} className="vp-card"><div className="vp-num">{i + 1}</div><div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div></div>
            ))}
          </div>
        </section>

        <div className="cs-banner">
          <div className="cs-badge">Coming Soon</div>
          <div><div className="cs-title">Instant Bookable Fabrics ⚡</div><div className="cs-desc">Ready-stock bookable in seconds — no waiting for quotes.</div></div>
        </div>

        <section className="section">
          <div className="section-label">How It Works</div>
          <div className="section-title">From requirement<br/>to delivery.</div>
          <div className="steps">
            {[
              { title: "Post Your Requirement", desc: "Fabric type, qty, GSM, construction — takes under 2 minutes." },
              { title: "Receive Competing Quotes", desc: "Multiple verified mills respond with live pricing and lead times." },
              { title: "Request Free Samples", desc: "Order samples at zero cost — tracked from dispatch to your door." },
              { title: "Place Order Securely", desc: "Pay Locofast — funds held until you confirm quality on delivery." },
              { title: "Track Every Step", desc: "Full logistics visibility from mill gate to your warehouse." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label">Trusted By</div>
          <div className="section-title">Leading Bangladesh<br/>groups source with us.</div>
          <div className="buyers-grid">
            {factories.slice(0, 8).map((name, i) => (<div key={i} className="buyer-chip">{name}</div>))}
            <div className="buyers-more">+ 100 more garment factories & buying houses</div>
          </div>
        </section>
      </div>
    </div>
  );
};

// India Credit Landing Page Component (Campaign 2)
const IndiaCreditLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    gstNumber: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  // Fire Lead pixel on thank you page
  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const industries = [
    "Garment Exporters", "Fashion Brands", "Apparel Manufacturers", "Fabric Traders",
    "Buying Houses", "Home Textiles", "Kids Wear", "Activewear Brands", "Uniform Manufacturers", "Export Houses"
  ];

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) {
      setGstError("Please enter a GST number");
      return;
    }
    
    if (!validateGST(formData.gstNumber.toUpperCase())) {
      setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV");
      return;
    }

    setGstLoading(true);
    setGstError("");
    setGstInfo(null);

    try {
      const response = await axios.post(`${API}/gst/lookup`, {
        gstin: formData.gstNumber.toUpperCase()
      });

      if (response.data.success) {
        setGstInfo(response.data.data);
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else {
        setGstError(response.data.error || "GST lookup failed");
      }
    } catch (error) {
      console.error("GST lookup error:", error);
      setGstError("Failed to fetch GST details. Please try again.");
    } finally {
      setGstLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid business email.";
    }
    if (!formData.phone.trim() || formData.phone.length !== 10) newErrors.phone = "Please enter a valid 10-digit number.";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "Please enter your GST number.";
    if (formData.gstNumber && !validateGST(formData.gstNumber.toUpperCase())) {
      newErrors.gstNumber = "Invalid GST format.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: "credit_buyer",
        phone: `+91${formData.phone}`,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-credit"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    { q: "Is this a loan? Do I need to approach a bank?", a: "No. This is not a loan. Locofast simply extends you flexible payment terms on your fabric orders — similar to trade credit. There is no bank involvement, no EMI, and no interest in the traditional sense." },
    { q: "Do I need to submit ITR or balance sheet?", a: "Not at the application stage. Once our team contacts you, we may ask for basic GST returns or bank statements depending on the limit you need — but the initial application is completely document-free." },
    { q: "What payment tenures are available?", a: "We offer 30, 60 and 90-day payment terms depending on your business profile and order volume. Longer tenures are available for established buyers." },
    { q: "What is the minimum order value?", a: "There is no strict minimum, but payment terms are typically offered on orders above ₹50,000. Our team will guide you based on your specific requirements." }
  ];

  return (
    <div className="landing-page india-credit">
      <div className="fold green-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <svg viewBox="0 0 16 16"><path d="M8 1l1.545 3.09L13 4.636l-2.5 2.436.59 3.428L8 8.91l-3.09 1.59.59-3.428L3 4.636l3.455-.546L8 1z" fill="white" opacity="0.9"/></svg>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Source Fabrics Now,<br/><em>Pay After Delivery</em></h1>
          <p className="hero-sub">Order from 500+ verified mills with flexible payment terms.<br/>No collateral. No upfront stress. Just grow.</p>
          
          <div className="how-pills">
            <div className="how-pill"><div className="how-pill-num">1</div> Order Fabric</div>
            <div className="how-pill"><div className="how-pill-num">2</div> Receive & Approve</div>
            <div className="how-pill"><div className="how-pill-num">3</div> Pay Later</div>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Supplying fabrics for India's biggest brands & manufacturers</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="terms-highlight">
              <div className="terms-icon">💳</div>
              <div>
                <div className="terms-title">Flexible payment terms available</div>
                <div className="terms-sub">Source more. Pay on your terms. No collateral needed.</div>
              </div>
            </div>

            <div className="form-head">Check your eligibility</div>
            <div className="form-sub">Tell us a bit about your business — we'll be in touch within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="name">Name <span className="req">*</span></label>
                <input type="text" id="name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="gstNumber">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="gstNumber" data-testid="gst-input" placeholder="27AAPFU0939F1ZV"
                    maxLength="15"
                    value={formData.gstNumber} 
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn credit" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card credit" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>
                      {gstInfo.status}
                    </span>
                    <span className="gst-verified credit">GST Verified</span>
                  </div>
                  <div className="gst-info-row">
                    <span className="gst-label credit">Legal Name</span>
                    <span className="gst-value">{gstInfo.legal_name || '-'}</span>
                  </div>
                  {gstInfo.trade_name && (
                    <div className="gst-info-row">
                      <span className="gst-label credit">Trade Name</span>
                      <span className="gst-value">{gstInfo.trade_name}</span>
                    </div>
                  )}
                  <div className="gst-info-row">
                    <span className="gst-label credit">Type</span>
                    <span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span>
                  </div>
                  {gstInfo.address && (
                    <div className="gst-info-row">
                      <span className="gst-label credit">Address</span>
                      <span className="gst-value small">{gstInfo.address}</span>
                    </div>
                  )}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="company">Company Name <span className="req">*</span></label>
                <input type="text" id="company" data-testid="company-input" placeholder="← Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">Email <span className="req">*</span></label>
                <input type="email" id="email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <button type="submit" className="submit-btn green" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Check My Eligibility"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free to apply · No credit score impact · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon green">✓</div>
            <div className="success-title">Application received!</div>
            <div className="success-sub">Our team will call you within 24 hours to discuss your payment terms. You can explore our full fabric catalogue in the meantime.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn green">Browse Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">₹50L+</div><div className="stat-lbl">Avg. credit<br/>per buyer</div></div>
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>mills</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Approval<br/>time</div></div>
        </div>

        <section className="section">
          <div className="section-label green-text">What You Get</div>
          <div className="section-title">Source more fabric.<br/>Pay on your schedule.</div>
          <div className="vp-list">
            {[
              { title: "No Upfront Payment Required", desc: "Order fabric from any of our 500+ mills and pay only after you receive and approve the quality.", highlight: true },
              { title: "No Collateral. No Guarantor.", desc: "Unsecured payment terms — no property, no FD, no personal guarantee needed to get started.", highlight: true },
              { title: "Flexible Repayment Tenure", desc: "Choose payment timelines that match your business cycle — 30, 60 or 90 day terms available.", highlight: true },
              { title: "5,000+ Fabric Options", desc: "Cotton, denim, synthetics, linen and more — from verified mills across Surat, Ahmedabad, Tiruppur and beyond." },
              { title: "Free Samples Before You Order", desc: "Request samples from any mill at zero cost, tracked from dispatch to your door." },
              { title: "Dedicated Account Manager", desc: "A sourcing expert helps you find the right fabric, negotiate rates, and manage your orders end to end." }
            ].map((vp, i) => (
              <div key={i} className={`vp-card ${vp.highlight ? "green-highlight" : ""}`}>
                <div className={`vp-num ${vp.highlight ? "green" : ""}`}>{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label green-text">Eligibility</div>
          <div className="section-title">Who can apply?</div>
          <div className="elig-card">
            <div className="elig-title">You're likely eligible if you —</div>
            <div className="elig-list">
              {[
                "Run a textile, garment or apparel business in India",
                "Have been in business for 1+ year",
                "Purchase fabrics regularly for your manufacturing or trading needs",
                "Have a GST-registered business (proprietorship, partnership or Pvt. Ltd.)"
              ].map((item, i) => (
                <div key={i} className="elig-item">
                  <div className="elig-check"><svg viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5 4-4" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                  <div className="elig-text">{item}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-label green-text">How It Works</div>
          <div className="section-title">Simple as 1, 2, 3.</div>
          <div className="steps">
            {[
              { title: "Apply in 2 minutes", desc: "Fill the form above. No financial documents needed upfront — just basic business details." },
              { title: "Get approved in 48 hours", desc: "Our team calls you, understands your sourcing needs, and sets up your payment terms." },
              { title: "Source fabric & pay later", desc: "Browse 5,000+ fabrics. Place orders. Receive, inspect, and pay within your agreed timeline." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle green">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label green-text">FAQ</div>
          <div className="section-title">Common questions.</div>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? "open" : ""}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                <div className="faq-q">
                  <span>{faq.q}</span>
                  <svg viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div className="faq-a">{faq.a}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="bottom-cta">
          <div className="bottom-cta-title">Ready to source smarter?</div>
          <div className="bottom-cta-sub">Join 700+ textile businesses already sourcing on flexible terms with Locofast.</div>
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="bottom-btn green">Check My Eligibility →</a>
        </div>
      </div>
    </div>
  );
};

// Admin Panel Component
const AdminPanel = () => {
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [appliedDateFilter, setAppliedDateFilter] = useState({ from: "", to: "" });
  const [campaignFilter, setCampaignFilter] = useState([]);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const campaignDropdownRef = useRef(null);

  const CAMPAIGN_OPTIONS = [
    { value: "bangladesh", label: "Bangladesh Generic" },
    { value: "bangladesh-shirt", label: "Bangladesh Shirt" },
    { value: "bangladesh-pants", label: "Bangladesh Pants/Chinos" },
    { value: "colombia-denim", label: "Colombia Denim" },
    { value: "srilanka-generic", label: "Sri Lanka" },
    { value: "vietnam-generic", label: "Vietnam" },
    { value: "india-credit", label: "India Credit" },
    { value: "india-denim", label: "India Denim" },
    { value: "india-shirt", label: "India Shirt" },
    { value: "india-denim-seller", label: "India Denim Seller" },
    { value: "india-shirt-seller", label: "India Shirt Seller" },
    { value: "india-seller", label: "India Seller (Generic)" },
    { value: "india-knits-seller-tirupur", label: "India Knits Seller (Tirupur)" },
    { value: "india-sportswear", label: "India Sportswear" },
    { value: "india-rayon", label: "India Rayon" },
    { value: "india-cotton", label: "India Cotton" },
    { value: "vendor-signup", label: "Vendor Sign-up" },
    { value: "locofast_rfq", label: "Website RFQ" },
  ];

  const toggleCampaign = (value) => {
    setCampaignFilter(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const isAllSelected = campaignFilter.length === 0;
  const filterHas = (...campaigns) => isAllSelected || campaigns.some(c => campaignFilter.includes(c));

  const applyFilters = (leadsData) => {
    let filtered = [...leadsData];
    
    // Campaign filter (multi-select: empty array = all)
    if (campaignFilter.length > 0) {
      filtered = filtered.filter(l => campaignFilter.includes(l.campaign));
    }
    
    // Date filter - compare date strings directly (YYYY-MM-DD) to avoid timezone issues
    if (appliedDateFilter.from) {
      filtered = filtered.filter(l => l.created_at.slice(0, 10) >= appliedDateFilter.from);
    }
    if (appliedDateFilter.to) {
      filtered = filtered.filter(l => l.created_at.slice(0, 10) <= appliedDateFilter.to);
    }
    
    setFilteredLeads(filtered);
  };

  const handleFilterSubmit = () => {
    setAppliedDateFilter({ ...dateFilter });
  };

  useEffect(() => {
    applyFilters(leads);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedDateFilter, campaignFilter, leads]);

  // Close campaign dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(e.target)) {
        setCampaignDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/auth`, { password });
      setIsAuthenticated(true);
      fetchLeads();
    } catch (err) {
      setError("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads?password=${password}`);
      setLeads(res.data);
    } catch (err) {
      setError("Failed to fetch leads");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    let url = `${API}/admin/leads/export?password=${password}`;
    if (campaignFilter.length > 0) url += `&campaign=${campaignFilter.join(",")}`;
    if (appliedDateFilter.from) url += `&from_date=${appliedDateFilter.from}`;
    if (appliedDateFilter.to) url += `&to_date=${appliedDateFilter.to}`;
    window.open(url, "_blank");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}?password=${password}`);
      setLeads(leads.filter(l => l.id !== id));
    } catch (err) {
      alert("Failed to delete lead");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatCompanyType = (type) => {
    const types = {
      garment_factory: "Garment Factory", buying_house: "Buying House", others: "Others",
      garment_manufacturer: "Garment Manufacturer", fabric_trader: "Fabric Trader", fashion_brand: "Fashion Brand", exporter: "Export House", other: "Other",
      denim_buyer: "Denim Buyer", shirt_buyer: "Shirt Buyer", fabric_wholesaler: "Fabric Wholesaler", credit_buyer: "Credit Buyer", denim_seller: "Denim Seller"
    };
    return types[type] || type;
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login" data-testid="admin-login">
        <div className="admin-login-card">
          <div className="admin-logo"><Users size={48} /></div>
          <h1>Locofast Admin</h1>
          <p>Enter password to access leads</p>
          <form onSubmit={handleLogin}>
            <input type="password" data-testid="admin-password-input" placeholder="Enter password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            {error && <div className="admin-error" data-testid="admin-error">{error}</div>}
            <button type="submit" data-testid="admin-login-btn" disabled={isLoading}>{isLoading ? "Verifying..." : "Access Dashboard"}</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Locofast Leads</h1>
          <span className="lead-count">{filteredLeads.length} of {leads.length}</span>
        </div>
        <div className="admin-header-right">
          <button className="export-btn" data-testid="export-csv-btn" onClick={handleExport}><Download size={18} />Export CSV</button>
          <button className="logout-btn" data-testid="logout-btn" onClick={() => { setIsAuthenticated(false); setPassword(""); }}><LogOut size={18} /></button>
        </div>
      </header>

      {/* Filters */}
      <div className="admin-filters">
        <div className="filter-group">
          <label><Calendar size={14} /> From</label>
          <input type="date" value={dateFilter.from} onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })} />
        </div>
        <div className="filter-group">
          <label><Calendar size={14} /> To</label>
          <input type="date" value={dateFilter.to} onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })} />
        </div>
        <div className="filter-group" ref={campaignDropdownRef}>
          <label><Filter size={14} /> Campaign</label>
          <div className="campaign-multiselect" data-testid="campaign-filter">
            <button type="button" className="campaign-multiselect-btn" data-testid="campaign-filter-btn"
              onClick={() => setCampaignDropdownOpen(!campaignDropdownOpen)}>
              <span>{isAllSelected ? "All Campaigns" : campaignFilter.length === 1 ? CAMPAIGN_OPTIONS.find(o => o.value === campaignFilter[0])?.label : `${campaignFilter.length} campaigns`}</span>
              <ChevronDown size={14} className={campaignDropdownOpen ? "chevron-up" : ""} />
            </button>
            {campaignDropdownOpen && (
              <div className="campaign-dropdown" data-testid="campaign-dropdown">
                <label className="campaign-option" data-testid="campaign-option-all">
                  <input type="checkbox" checked={isAllSelected} onChange={() => setCampaignFilter([])} />
                  <span>All Campaigns</span>
                </label>
                <div className="campaign-divider" />
                {CAMPAIGN_OPTIONS.map(opt => (
                  <label key={opt.value} className="campaign-option" data-testid={`campaign-option-${opt.value}`}>
                    <input type="checkbox" checked={campaignFilter.includes(opt.value)} onChange={() => toggleCampaign(opt.value)} />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <button className="submit-filters-btn" data-testid="submit-filters-btn" onClick={handleFilterSubmit}>Submit</button>
        {(appliedDateFilter.from || appliedDateFilter.to || campaignFilter.length > 0) && (
          <button className="clear-filters-btn" onClick={() => { setDateFilter({ from: "", to: "" }); setAppliedDateFilter({ from: "", to: "" }); setCampaignFilter([]); }}>
            Clear Filters
          </button>
        )}
      </div>

      <main className="admin-main">
        {isLoading ? (
          <div className="admin-loading">Loading leads...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="admin-empty" data-testid="empty-state"><Users size={64} /><h2>{leads.length === 0 ? "No leads yet" : "No leads match filters"}</h2><p>{leads.length === 0 ? "Leads from your landing pages will appear here" : "Try adjusting your date or campaign filters"}</p></div>
        ) : (
          <div className="leads-table-wrap">
            <table className="leads-table" data-testid="leads-table">
              <thead>
                <tr>
                  <th><Clock size={14} /> Submitted</th>
                  {(isAllSelected || campaignFilter.length > 1) && <th><Tag size={14} /> Campaign</th>}
                  {filterHas("bangladesh-shirt","bangladesh-pants","colombia-denim","srilanka-generic","vietnam-generic","india-credit","india-denim","india-shirt","india-denim-seller","india-shirt-seller","india-seller","india-knits-seller-tirupur","india-sportswear","india-rayon","india-cotton","vendor-signup","locofast_rfq") && <th>Name</th>}
                  <th><Building2 size={14} /> Company</th>
                  <th><Mail size={14} /> Email</th>
                  {filterHas("bangladesh","bangladesh-shirt","bangladesh-pants","colombia-denim","srilanka-generic","vietnam-generic") && <th>Type</th>}
                  {filterHas("locofast_rfq","srilanka-generic","vietnam-generic","india-seller") && <th>Fabric Type</th>}
                  <th><Phone size={14} /> Phone</th>
                  {filterHas("bangladesh-shirt","bangladesh-pants","colombia-denim","srilanka-generic","vietnam-generic") && <th><ExternalLink size={14} /> Website</th>}
                  {filterHas("india-denim-seller","india-shirt-seller","india-seller","india-knits-seller-tirupur","vendor-signup") && <th>City</th>}
                  {filterHas("bangladesh") && <th>BIN</th>}
                  {filterHas("vendor-signup") && <th>Monthly Capacity</th>}
                  {filterHas("vendor-signup") && <th>Categories</th>}
                  {filterHas("india-credit","india-denim","india-shirt","india-denim-seller","india-shirt-seller","india-seller","india-knits-seller-tirupur","india-sportswear","india-rayon","india-cotton","locofast_rfq") && <th>GST</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <td className="date-cell">{formatDate(lead.created_at)}</td>
                    {(isAllSelected || campaignFilter.length > 1) && <td><span className={`campaign-badge ${lead.campaign}`}>{lead.campaign}</span></td>}
                    {filterHas("bangladesh-shirt","bangladesh-pants","colombia-denim","srilanka-generic","vietnam-generic","india-credit","india-denim","india-shirt","india-denim-seller","india-shirt-seller","india-seller","india-knits-seller-tirupur","india-sportswear","india-rayon","india-cotton","vendor-signup","locofast_rfq") && <td className="name-cell">{lead.name || "—"}</td>}
                    <td className="company-cell">{lead.company}</td>
                    <td className="email-cell">{lead.email}</td>
                    {filterHas("bangladesh","bangladesh-shirt","bangladesh-pants","colombia-denim","srilanka-generic","vietnam-generic") && <td><span className="type-badge">{formatCompanyType(lead.company_type)}</span></td>}
                    {filterHas("locofast_rfq","srilanka-generic","vietnam-generic","india-seller") && <td className="fabric-cell">{lead.fabric_type || "—"}</td>}
                    <td className="phone-cell">{lead.phone || "—"}</td>
                    {filterHas("bangladesh-shirt","bangladesh-pants","colombia-denim","srilanka-generic","vietnam-generic") && <td className="website-cell">{lead.website ? <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer">{lead.website}</a> : "—"}</td>}
                    {filterHas("india-denim-seller","india-shirt-seller","india-seller","india-knits-seller-tirupur","vendor-signup") && <td>{lead.city || "—"}</td>}
                    {filterHas("bangladesh") && <td>{lead.bin_number || "—"}</td>}
                    {filterHas("vendor-signup") && <td>{lead.monthly_capacity || "—"}</td>}
                    {filterHas("vendor-signup") && <td>{lead.categories || "—"}</td>}
                    {filterHas("india-credit","india-denim","india-shirt","india-denim-seller","india-shirt-seller","india-seller","india-knits-seller-tirupur","india-sportswear","india-rayon","india-cotton","locofast_rfq") && (
                      <td className="gst-cell">
                        {lead.gst_number ? (
                          <div className="gst-info-mini">
                            <span className="gst-num">{lead.gst_number}</span>
                            {lead.gst_info && (
                              <span className={`gst-status-mini ${lead.gst_info.status === 'Active' ? 'active' : ''}`}>
                                {lead.gst_info.status}
                              </span>
                            )}
                          </div>
                        ) : "—"}
                      </td>
                    )}
                    <td><button className="delete-btn" data-testid={`delete-btn-${lead.id}`} onClick={() => handleDelete(lead.id)}><Trash2 size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

// Denim Landing Page Component (Campaign 3)
const DenimLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    gstNumber: "",
    email: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  // Fire Lead pixel on thank you page
  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const denimTypes = [
    "Raw Denim", "Stretch Denim", "Organic Denim",
    "Recycled Denim", "Coated Denim", "Printed Denim", "Chambray"
  ];

  const compositions = [
    "100% Cotton", "Cotton Polyester", "Cotton Spandex", "Cotton Lycra"
  ];

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) {
      setGstError("Please enter a GST number");
      return;
    }
    
    if (!validateGST(formData.gstNumber.toUpperCase())) {
      setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV");
      return;
    }

    setGstLoading(true);
    setGstError("");
    setGstInfo(null);

    try {
      const response = await axios.post(`${API}/gst/lookup`, {
        gstin: formData.gstNumber.toUpperCase()
      });

      if (response.data.success) {
        setGstInfo(response.data.data);
        // Auto-fill company name if empty
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else {
        setGstError(response.data.error || "GST lookup failed");
      }
    } catch (error) {
      console.error("GST lookup error:", error);
      setGstError("Failed to fetch GST details. Please try again.");
    } finally {
      setGstLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim() || formData.phone.length !== 10) newErrors.phone = "Please enter a valid 10-digit phone number.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "Please enter your GST number.";
    if (formData.gstNumber && !validateGST(formData.gstNumber.toUpperCase())) {
      newErrors.gstNumber = "Invalid GST format.";
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Please use your official company email address (e.g. name@yourcompany.com).";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: "denim_buyer",
        phone: `+91${formData.phone}`,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-denim"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page denim-page">
      <div className="fold denim-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <svg viewBox="0 0 16 16"><path d="M8 1l1.545 3.09L13 4.636l-2.5 2.436.59 3.428L8 8.91l-3.09 1.59.59-3.428L3 4.636l3.455-.546L8 1z" fill="white" opacity="0.9"/></svg>
            <span>India's Premium Denim Sourcing Platform</span>
          </div>
          <h1>Source Premium<br/><em>Denim Fabrics</em><br/>Direct from Mills</h1>
          <p className="hero-sub">Access 50+ verified denim mills across India.<br/>Raw, stretch, selvedge & sustainable options.</p>
          
          <div className="denim-chips">
            {denimTypes.slice(0, 4).map((type, i) => (
              <span key={i} className="denim-chip">{type}</span>
            ))}
          </div>
          <div className="denim-chips composition-chips">
            {compositions.map((comp, i) => (
              <span key={i} className="denim-chip comp">{comp}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Supplying denim for India's biggest brands</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Denim Best Rates & Samples</div>
            <div className="form-sub">Fill in your details — our denim experts will reach out within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="name">Name <span className="req">*</span></label>
                <input type="text" id="name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="gstNumber">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="gstNumber" data-testid="gst-input" placeholder="27AAPFU0939F1ZV"
                    maxLength="15"
                    value={formData.gstNumber} 
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>
                      {gstInfo.status}
                    </span>
                    <span className="gst-verified">GST Verified</span>
                  </div>
                  <div className="gst-info-row">
                    <span className="gst-label">Legal Name</span>
                    <span className="gst-value">{gstInfo.legal_name || '-'}</span>
                  </div>
                  {gstInfo.trade_name && (
                    <div className="gst-info-row">
                      <span className="gst-label">Trade Name</span>
                      <span className="gst-value">{gstInfo.trade_name}</span>
                    </div>
                  )}
                  <div className="gst-info-row">
                    <span className="gst-label">Type</span>
                    <span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span>
                  </div>
                  {gstInfo.address && (
                    <div className="gst-info-row">
                      <span className="gst-label">Address</span>
                      <span className="gst-value small">{gstInfo.address}</span>
                    </div>
                  )}
                  {gstInfo.registration_date && (
                    <div className="gst-info-row">
                      <span className="gst-label">Reg. Date</span>
                      <span className="gst-value">{gstInfo.registration_date}</span>
                    </div>
                  )}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="company">Company Name <span className="req">*</span></label>
                <input type="text" id="company" data-testid="company-input" placeholder="← Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">Email ID <span className="req">*</span></label>
                <input type="email" id="email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <button type="submit" className="submit-btn denim" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Best Rates & Samples"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples · No commitment · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon denim">✓</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our denim sourcing team will contact you within 24 hours with sample options and pricing.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn denim">Browse Denim Collection →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">50+</div><div className="stat-lbl">Denim<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Fabric<br/>Varieties</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Sample<br/>Delivery</div></div>
        </div>

        <section className="section">
          <div className="section-label denim-text">Denim Range</div>
          <div className="section-title">Every denim type<br/>you need.</div>
          <div className="vp-list">
            {[
              { title: "Raw & Rigid Denim", desc: "Unwashed, untreated denim in various weights from 8oz to 16oz for authentic vintage looks." },
              { title: "Stretch Denim", desc: "Comfort stretch with 1-3% elastane blends. Perfect for modern fits and athleisure styles." },
              { title: "Sustainable Denim", desc: "Organic cotton, recycled fibers, and eco-friendly washes. BCI and GOTS certified options." },
              { title: "Coated & Treated", desc: "Waxed, resin-coated, and specialty finishes for fashion-forward applications." },
              { title: "Lightweight Chambray", desc: "4-6oz lightweight denim perfect for shirts, dresses, and summer collections." },
              { title: "Printed Denim", desc: "Custom prints and patterns on denim base for unique fashion statements." }
            ].map((vp, i) => (
              <div key={i} className="vp-card denim-highlight">
                <div className="vp-num denim">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label denim-text">How It Works</div>
          <div className="section-title">From inquiry to<br/>delivery.</div>
          <div className="steps">
            {[
              { title: "Submit Your Requirements", desc: "Tell us your denim specs — weight, stretch, wash, quantity." },
              { title: "Get Matched with Mills", desc: "We connect you with the best mills for your specific needs." },
              { title: "Receive Free Samples", desc: "Get physical swatches delivered to your doorstep at no cost." },
              { title: "Place Bulk Order", desc: "Finalize pricing and place your order with payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle denim">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// Bangladesh Men Shirt Landing Page Component
const BangladeshShirtLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    companyType: "",
    email: "",
    website: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Fire Lead pixel on thank you page
  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const shirtTypes = [
    "Formal Shirts", "Casual Shirts", "Dress Shirts", "Oxford Shirts",
    "Linen Shirts", "Denim Shirts", "Polo Shirts", "Printed Shirts"
  ];

  const fabrics = [
    "100% Cotton", "Cotton Blend", "Linen", "Chambray"
  ];

  const factories = [
    "Ananta Group", "Goldstar Group", "Snowtex", "Hameem Group", "Windy Group",
    "Sharmin Group", "Spider Group", "Ram Group", "Valmont Group", "Florence Group"
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.companyType) newErrors.companyType = "Please select your company type.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Please use your official company email (not Gmail, Yahoo, etc.)";
    }
    if (!formData.website.trim()) {
      newErrors.website = "Please enter your company website.";
    } else if (!/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.website)) {
      newErrors.website = "Please enter a valid website URL.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: formData.companyType,
        phone: `+880${formData.phone}`,
        website: formData.website,
        campaign: "bangladesh-shirt"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page shirt-page">
      <div className="fold shirt-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <svg viewBox="0 0 16 16"><path d="M8 1l1.545 3.09L13 4.636l-2.5 2.436.59 3.428L8 8.91l-3.09 1.59.59-3.428L3 4.636l3.455-.546L8 1z" fill="white" opacity="0.9"/></svg>
            <span>Trusted by 500+ Bangladesh Garment Factories</span>
          </div>
          <h1>Source Premium<br/><em>Men's Shirt Fabrics</em><br/>Direct from India</h1>
          <p className="hero-sub">Access 500+ verified mills for shirting fabrics.<br/>Best rates, free samples & secure payments.</p>
          
          <div className="denim-chips">
            {shirtTypes.slice(0, 4).map((type, i) => (
              <span key={i} className="denim-chip">{type}</span>
            ))}
          </div>
          <div className="denim-chips composition-chips">
            {fabrics.map((fab, i) => (
              <span key={i} className="denim-chip comp">{fab}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Premium shirting fabrics available</div>
          <div className="marquee-track">
            {[...shirtTypes, ...shirtTypes, ...shirtTypes].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Best Rates & Samples</div>
            <div className="form-sub">Fill in your details — our shirting experts will reach out within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="name">Name <span className="req">*</span></label>
                <input type="text" id="name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+880</div>
                  <input type="tel" id="phone" data-testid="phone-input" placeholder="1XXXXXXXXX" maxLength="11"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="company">Company Name <span className="req">*</span></label>
                <input type="text" id="company" data-testid="company-input" placeholder="Your company name"
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                {errors.company && <div className="field-error">{errors.company}</div>}
              </div>

              <div className={`field ${errors.companyType ? "error" : ""}`}>
                <label htmlFor="companyType">Company Type <span className="req">*</span></label>
                <div className="select-wrapper">
                  <select id="companyType" data-testid="company-type-select" value={formData.companyType}
                    onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}>
                    <option value="" disabled>Select your company type</option>
                    <option value="garment_manufacturer">Garment Manufacturer</option>
                    <option value="buying_house">Buying House</option>
                    <option value="fabric_wholesaler">Fabric Wholesaler</option>
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                {errors.companyType && <div className="field-error">{errors.companyType}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">Official Email ID <span className="req">*</span></label>
                <input type="email" id="email" data-testid="email-input" placeholder="you@yourcompany.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <div className="field-hint">Use your company email (Gmail, Yahoo not accepted)</div>
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.website ? "error" : ""}`}>
                <label htmlFor="website">Company Website <span className="req">*</span></label>
                <input type="text" id="website" data-testid="website-input" placeholder="www.yourcompany.com"
                  value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                {errors.website && <div className="field-error">{errors.website}</div>}
              </div>

              <button type="submit" className="submit-btn shirt" data-testid="submit-btn" disabled={isSubmitting}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Best Rates & Samples"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples · No commitment · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon shirt">✓</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our shirting experts will contact you within 24 hours with sample options and best rates.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn shirt">Browse Shirting Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">1000+</div><div className="stat-lbl">Fabric<br/>Options</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Sample<br/>Delivery</div></div>
        </div>

        <section className="section">
          <div className="section-label shirt-text">Shirting Range</div>
          <div className="section-title">Every shirting fabric<br/>you need.</div>
          <div className="vp-list">
            {[
              { title: "Premium Cotton Shirting", desc: "60s, 80s, 100s count cotton in poplin, twill, and dobby weaves for formal shirts." },
              { title: "Oxford & Chambray", desc: "Classic oxford cloth and chambray fabrics for casual and semi-formal shirts." },
              { title: "Linen & Linen Blends", desc: "Pure linen and cotton-linen blends for summer collections and resort wear." },
              { title: "Printed & Yarn Dyed", desc: "Checks, stripes, and prints for trendy casual shirt collections." },
              { title: "Stretch Shirting", desc: "Cotton-spandex blends for comfort fit formal and casual shirts." },
              { title: "Sustainable Options", desc: "Organic cotton, BCI certified, and recycled fabric options available." }
            ].map((vp, i) => (
              <div key={i} className="vp-card shirt-highlight">
                <div className="vp-num shirt">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label shirt-text">Trusted By</div>
          <div className="section-title">Leading Bangladesh<br/>factories source with us.</div>
          <div className="buyers-grid">
            {factories.map((name, i) => (<div key={i} className="buyer-chip">{name}</div>))}
            <div className="buyers-more">+ 500 more garment factories</div>
          </div>
        </section>

        <section className="section">
          <div className="section-label shirt-text">How It Works</div>
          <div className="section-title">From inquiry to<br/>delivery.</div>
          <div className="steps">
            {[
              { title: "Submit Your Requirements", desc: "Tell us your shirting specs — fabric type, count, weave, quantity." },
              { title: "Get Matched with Mills", desc: "We connect you with the best mills for your specific needs." },
              { title: "Receive Free Samples", desc: "Get physical swatches delivered to your doorstep at no cost." },
              { title: "Place Bulk Order", desc: "Finalize pricing and place your order with payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle shirt">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// Bangladesh Pants / Chino Fabrics Landing Page Component
const BangladeshPantsLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    companyType: "",
    email: "",
    website: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const gsmOptions = [
    { gsm: "160 GSM", label: "Light Chino Twill", tag: "FREE" },
    { gsm: "200 GSM", label: "Mid-Weight Pant", tag: "FREE" },
    { gsm: "240 GSM", label: "Heavy Chino", tag: "FREE" },
    { gsm: "280 GSM", label: "Bottom-Weight Canvas", tag: "FREE" }
  ];

  const factories = [
    "Ananta Group", "Goldstar Group", "Snowtex", "Hameem Group", "Windy Group",
    "Sharmin Group", "Spider Group", "Ram Group", "Valmont Group", "Florence Group"
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.companyType) newErrors.companyType = "Please select your company type.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Please use your official company email (not Gmail, Yahoo, etc.)";
    }
    if (!formData.website.trim()) {
      newErrors.website = "Please enter your company website.";
    } else if (!/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.website)) {
      newErrors.website = "Please enter a valid website URL.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: formData.companyType,
        phone: `+880${formData.phone}`,
        website: formData.website,
        campaign: "bangladesh-pants"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page pants-page">
      <div className="fold pants-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag">100% COTTON</span>
            <span>Trusted by 500+ Bangladesh Garment Factories</span>
          </div>
          <h1>Source Pant & Chino<br/><em>Fabrics Directly</em><br/>from Indian Mills</h1>
          <p className="hero-sub">Real mill-gate prices. No middlemen. One platform.</p>
          <p className="hero-sub-bengali">সরাসরি কারখানার মূল্যে · কোনো এজেন্ট নেই</p>
          
          <div className="denim-chips">
            <span className="denim-chip">50+ Verified Mills</span>
            <span className="denim-chip">Transparent Pricing</span>
            <span className="denim-chip">Reliable Supply Across GSMs</span>
          </div>
          <div className="denim-chips composition-chips">
            {["160 GSM", "200 GSM", "240 GSM", "280 GSM"].map((gsm, i) => (
              <span key={i} className="denim-chip comp">{gsm}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Premium chino & pant fabrics available</div>
          <div className="marquee-track">
            {[...factories, ...factories, ...factories].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Free Quotes & Samples</div>
            <div className="form-sub">Tell us your requirements — our chino fabric experts will reach out within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="pants-name">Name <span className="req">*</span></label>
                <input type="text" id="pants-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="pants-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+880</div>
                  <input type="tel" id="pants-phone" data-testid="phone-input" placeholder="1XXXXXXXXX" maxLength="11"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="pants-company">Company Name <span className="req">*</span></label>
                <input type="text" id="pants-company" data-testid="company-input" placeholder="Your company name"
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                {errors.company && <div className="field-error">{errors.company}</div>}
              </div>

              <div className={`field ${errors.companyType ? "error" : ""}`}>
                <label htmlFor="pants-companyType">Company Type <span className="req">*</span></label>
                <div className="select-wrapper">
                  <select id="pants-companyType" data-testid="company-type-select" value={formData.companyType}
                    onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}>
                    <option value="" disabled>Select your company type</option>
                    <option value="garment_manufacturer">Garment Manufacturer</option>
                    <option value="buying_house">Buying House</option>
                    <option value="fabric_wholesaler">Fabric Wholesaler</option>
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                {errors.companyType && <div className="field-error">{errors.companyType}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="pants-email">Official Email ID <span className="req">*</span></label>
                <input type="email" id="pants-email" data-testid="email-input" placeholder="you@yourcompany.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <div className="field-hint">Use your company email (Gmail, Yahoo not accepted)</div>
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.website ? "error" : ""}`}>
                <label htmlFor="pants-website">Company Website <span className="req">*</span></label>
                <input type="text" id="pants-website" data-testid="website-input" placeholder="www.yourcompany.com"
                  value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                {errors.website && <div className="field-error">{errors.website}</div>}
              </div>

              <button type="submit" className="submit-btn pants" data-testid="submit-btn" disabled={isSubmitting}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Free Quotes"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples · No commitment · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon pants">✓</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our chino fabric experts will contact you within 24 hours with sample options and best rates.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn pants">Browse Chino Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">50+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">160–280</div><div className="stat-lbl">GSM<br/>Range</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Sample<br/>Delivery</div></div>
        </div>

        <section className="section">
          <div className="section-label pants-text">Zero Cost Sampling</div>
          <div className="section-title">Feel It Before You Buy.</div>
          <p className="section-subtitle">Free samples from 50+ Indian mills. Any GSM available.</p>
          <p className="section-subtitle-bengali">বিনামূল্যে স্যাম্পল · ভারতীয় মিল থেকে সরাসরি</p>
          <div className="vp-list">
            {gsmOptions.map((opt, i) => (
              <div key={i} className="vp-card pants-highlight">
                <div className="vp-num pants">{opt.gsm}</div>
                <div>
                  <div className="vp-title">{opt.label}</div>
                  <div className="vp-desc"><span className="free-tag">{opt.tag}</span></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label pants-text">Money Safety Guarantee</div>
          <div className="section-title">Touch. Approve. Then Pay.</div>
          <p className="section-subtitle">Payment released only after you approve your chino fabric.</p>
          <p className="section-subtitle-bengali">আপনার অনুমোদনের পরেই পেমেন্ট · কোনো ঝুঁকি নেই</p>
          <div className="steps">
            {[
              { title: "Order Placed, Payment Held", desc: "Your payment is secured in escrow. The mill gets notified and starts production.", badge: "Secured" },
              { title: "Chino Fabric Dispatched", desc: "Track your shipment in real-time from the Indian mill to your factory.", badge: "In Transit" },
              { title: "You Receive & Approve", desc: "Inspect fabric quality. Payment is released only after your approval.", badge: "Pending" }
            ].map((step, i) => (
              <div key={i} className="step">
                <div className="step-circle pants">{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div className="step-title">{step.title} <span className={`step-badge ${step.badge.toLowerCase().replace(' ', '-')}`}>{step.badge}</span></div>
                  <div className="step-desc">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label pants-text">Trusted By</div>
          <div className="section-title">Leading Bangladesh<br/>factories source with us.</div>
          <div className="buyers-grid">
            {factories.map((name, i) => (<div key={i} className="buyer-chip">{name}</div>))}
            <div className="buyers-more">+ 500 more garment factories</div>
          </div>
        </section>

        <section className="section">
          <div className="section-label pants-text">How It Works</div>
          <div className="section-title">From inquiry to<br/>delivery.</div>
          <div className="steps">
            {[
              { title: "Submit Your Requirements", desc: "Tell us your specs — fabric GSM, weave, quantity, and delivery timeline." },
              { title: "Get Matched with Mills", desc: "We connect you with the best Indian mills for your chino fabric needs." },
              { title: "Receive Free Samples", desc: "Get physical swatches in all GSMs delivered to your doorstep at no cost." },
              { title: "Place Bulk Order", desc: "Finalize pricing and place your order with payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle pants">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};


// Colombia Denim Landing Page Component (Spanish)
const ColombiaDenimLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    companyType: "",
    email: "",
    website: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const denimSamples = [
    { name: "Índigo 12oz", desc: "Denim rígido clásico", tag: "GRATIS" },
    { name: "Stretch 9oz", desc: "Elástico premium", tag: "GRATIS" },
    { name: "Crudo 14oz", desc: "Denim crudo pesado", tag: "GRATIS" },
    { name: "Negro 10oz", desc: "Negro profundo", tag: "GRATIS" }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Por favor ingresa tu nombre.";
    if (!formData.phone.trim()) newErrors.phone = "Por favor ingresa tu número de teléfono.";
    if (!formData.company.trim()) newErrors.company = "Por favor ingresa el nombre de tu empresa.";
    if (!formData.companyType) newErrors.companyType = "Por favor selecciona el tipo de empresa.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Por favor ingresa un correo válido.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Usa tu correo corporativo (Gmail, Yahoo no aceptado)";
    }
    if (!formData.website.trim()) {
      newErrors.website = "Por favor ingresa el sitio web de tu empresa.";
    } else if (!/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.website)) {
      newErrors.website = "Por favor ingresa una URL válida.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: formData.companyType,
        phone: `+57${formData.phone}`,
        website: formData.website,
        campaign: "colombia-denim"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Algo salió mal. Por favor intenta de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page colombia-page">
      <div className="fold colombia-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-co">500+ FÁBRICAS</span>
            <span>India · Precios directos de fábrica</span>
          </div>
          <h1>Compra Denim de India.<br/><em>Sin Intermediarios.</em></h1>
          <p className="hero-sub">Precios directos de fábrica. Sin intermediarios. Una plataforma.</p>
          <p className="hero-cities">Medellín · Bogotá · Cali</p>
          
          <div className="denim-chips">
            <span className="denim-chip co-check">500+ proveedores verificados</span>
            <span className="denim-chip co-check">Garantía de dinero seguro</span>
            <span className="denim-chip co-check">Reserva inmediata</span>
          </div>
          <div className="denim-chips composition-chips">
            {["Índigo", "Stretch", "Crudo", "Negro"].map((type, i) => (
              <span key={i} className="denim-chip comp">{type}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Proveedores de telas para las marcas más grandes del mundo</div>
          <div className="marquee-track">
            {[...GLOBAL_BRAND_TICKER.map(n => n.replace("Manufacturers", "Fabricantes").replace("Private Labels", "Marcas Propias")), ...GLOBAL_BRAND_TICKER.map(n => n.replace("Manufacturers", "Fabricantes").replace("Private Labels", "Marcas Propias"))].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Cotiza Gratis</div>
            <div className="form-sub">Cuéntanos tus necesidades — nuestros expertos en denim te contactarán en 24 horas.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="co-name">Nombre <span className="req">*</span></label>
                <input type="text" id="co-name" data-testid="name-input" placeholder="Tu nombre completo"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="co-phone">Teléfono <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+57</div>
                  <input type="tel" id="co-phone" data-testid="phone-input" placeholder="3XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="co-company">Empresa <span className="req">*</span></label>
                <input type="text" id="co-company" data-testid="company-input" placeholder="Nombre de tu empresa"
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                {errors.company && <div className="field-error">{errors.company}</div>}
              </div>

              <div className={`field ${errors.companyType ? "error" : ""}`}>
                <label htmlFor="co-companyType">Tipo de Empresa <span className="req">*</span></label>
                <div className="select-wrapper">
                  <select id="co-companyType" data-testid="company-type-select" value={formData.companyType}
                    onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}>
                    <option value="" disabled>Selecciona el tipo de empresa</option>
                    <option value="garment_manufacturer">Fabricante de Ropa</option>
                    <option value="buying_house">Casa de Compra</option>
                    <option value="fabric_wholesaler">Mayorista de Telas</option>
                    <option value="brand">Marca / Diseñador</option>
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                {errors.companyType && <div className="field-error">{errors.companyType}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="co-email">Correo Corporativo <span className="req">*</span></label>
                <input type="email" id="co-email" data-testid="email-input" placeholder="tu@empresa.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <div className="field-hint">Usa tu correo corporativo (Gmail, Yahoo no aceptado)</div>
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.website ? "error" : ""}`}>
                <label htmlFor="co-website">Sitio Web de la Empresa <span className="req">*</span></label>
                <input type="text" id="co-website" data-testid="website-input" placeholder="www.tuempresa.com"
                  value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                {errors.website && <div className="field-error">{errors.website}</div>}
              </div>

              <button type="submit" className="submit-btn colombia" data-testid="submit-btn" disabled={isSubmitting}>
                <span className="btn-text">{isSubmitting ? "Enviando..." : "Cotiza Gratis"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Muestras gratis · Sin compromiso · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Términos aplican</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon colombia">✓</div>
            <div className="success-title">¡Solicitud Recibida!</div>
            <div className="success-sub">Nuestros expertos en denim te contactarán en 24 horas con opciones de muestras y los mejores precios.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn colombia">Explorar Denim →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip co-stats">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Fábricas<br/>verificadas</div></div>
          <div className="stat"><div className="stat-val">0%</div><div className="stat-lbl">Riesgo<br/>de fraude</div></div>
          <div className="stat"><div className="stat-val">48h</div><div className="stat-lbl">Primera<br/>cotización</div></div>
        </div>

        <section className="section">
          <div className="section-label colombia-text">SIN COSTO · SIN COMPROMISO</div>
          <div className="section-title">Muestras gratis de<br/>denim de India.</div>
          <p className="section-subtitle">Todo tipo de denim. De cualquier fábrica.</p>
          <p className="section-subtitle co-types">Denim índigo · Stretch · Crudo · Negro</p>
          <div className="vp-list">
            {denimSamples.map((sample, i) => (
              <div key={i} className="vp-card colombia-highlight">
                <div className="vp-num colombia-sample">{sample.name}</div>
                <div>
                  <div className="vp-title">{sample.desc}</div>
                  <div className="vp-desc"><span className="free-tag-co">{sample.tag}</span></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label colombia-text">GARANTÍA DE DINERO SEGURO</div>
          <div className="section-title">Paga solo cuando<br/>apruebes la calidad.</div>
          <p className="section-subtitle">Tu dinero está protegido hasta que confirmes.</p>
          <p className="section-subtitle co-types">Cero riesgo · Cero fraude</p>
          <div className="steps">
            {[
              { title: "Pedido colocado, dinero retenido", desc: "Pago asegurado con Locofast.", badge: "Asegurado", cls: "secured" },
              { title: "Denim despachado de fábrica", desc: "Seguimiento en tiempo real de tu envío.", badge: "En Tránsito", cls: "in-transit" },
              { title: "Recibes y apruebas calidad", desc: "Solo entonces se libera el pago al proveedor.", badge: "Pendiente", cls: "pending" }
            ].map((step, i) => (
              <div key={i} className="step">
                <div className="step-circle colombia">{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div className="step-title">{step.title} <span className={`step-badge ${step.cls}`}>{step.badge}</span></div>
                  <div className="step-desc">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label colombia-text">CÓMO FUNCIONA</div>
          <div className="section-title">De tu consulta a<br/>la entrega.</div>
          <div className="steps">
            {[
              { title: "Envía tus requisitos", desc: "Cuéntanos tus especificaciones de denim — tipo, peso, cantidad." },
              { title: "Te conectamos con fábricas", desc: "Encontramos las mejores fábricas en India para tus necesidades." },
              { title: "Recibe muestras gratis", desc: "Obtén muestras físicas enviadas a tu puerta sin costo." },
              { title: "Haz tu pedido", desc: "Finaliza precios y coloca tu pedido con protección de pago." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle colombia">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};


// India Polyester Knits / Sportswear Landing Page Component
const IndiaSportswearLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    gstNumber: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const fabricTypes = [
    { name: "Four Way Lycra", spec: "88% Polyester, 12% Spandex", gsm: "220–260 GSM", use: "Athleisure, Yoga Wear" },
    { name: "NS Lycra", spec: "92% Nylon, 8% Spandex", gsm: "170–200 GSM", use: "Leggings, Active Bottoms" },
    { name: "Rice Knit", spec: "100% Polyester Textured", gsm: "180–220 GSM", use: "Sports T-Shirts, Jerseys" },
    { name: "Spun Matty", spec: "100% Spun Polyester", gsm: "200–240 GSM", use: "Polo Shirts, Team Wear" }
  ];

  const industries = [
    "Sportswear Brands", "Activewear Manufacturers", "Athleisure Labels", "Team Uniform Makers",
    "Fitness Apparel", "Yoga Wear Brands", "School Sports Uniforms", "Export Houses",
    "Gym Wear Manufacturers", "Track Suit Makers"
  ];

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV"); return; }

    setGstLoading(true);
    setGstError("");
    setGstInfo(null);

    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        setGstInfo(response.data.data);
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else {
        setGstError(response.data.error || "GST lookup failed");
      }
    } catch (error) {
      setGstError("Failed to fetch GST details. Please try again.");
    } finally {
      setGstLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid business email.";
    if (!formData.phone.trim() || formData.phone.length !== 10) newErrors.phone = "Please enter a valid 10-digit number.";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "Please enter your GST number.";
    if (formData.gstNumber && !validateGST(formData.gstNumber.toUpperCase())) newErrors.gstNumber = "Invalid GST format.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: "sportswear_buyer",
        phone: `+91${formData.phone}`,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-sportswear"
      });
      setIsSuccess(true);
    } catch (error) {
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page india-sportswear">
      <div className="fold sportswear-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-sp">POLYESTER KNITS</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Sportswear Fabrics for<br/><em>Modern Activewear</em></h1>
          <p className="hero-sub">Source polyester knits directly from verified mills. Competitive pricing, reliable quality, smooth supply.</p>
          
          <div className="denim-chips">
            <span className="denim-chip sp-chip">Four Way Lycra</span>
            <span className="denim-chip sp-chip">NS Lycra</span>
            <span className="denim-chip sp-chip">Rice Knit</span>
            <span className="denim-chip sp-chip">Spun Matty</span>
          </div>
          <div className="denim-chips composition-chips">
            <span className="denim-chip comp">Cash or Credit</span>
            <span className="denim-chip comp">Free Samples</span>
            <span className="denim-chip comp">500+ Mills</span>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Supplying sportswear fabrics for India's top brands</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Free Samples & Best Rates</div>
            <div className="form-sub">Tell us your sportswear fabric needs — our knit experts will reach out within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="sp-name">Name <span className="req">*</span></label>
                <input type="text" id="sp-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="sp-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="sp-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="sp-gst">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="sp-gst" data-testid="gst-input" placeholder="27AAPFU0939F1ZV" maxLength="15"
                    value={formData.gstNumber}
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn sportswear" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card sportswear" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>
                      {gstInfo.status}
                    </span>
                    <span className="gst-verified sportswear">GST Verified</span>
                  </div>
                  <div className="gst-info-row">
                    <span className="gst-label sportswear">Legal Name</span>
                    <span className="gst-value">{gstInfo.legal_name || '-'}</span>
                  </div>
                  {gstInfo.trade_name && (
                    <div className="gst-info-row">
                      <span className="gst-label sportswear">Trade Name</span>
                      <span className="gst-value">{gstInfo.trade_name}</span>
                    </div>
                  )}
                  <div className="gst-info-row">
                    <span className="gst-label sportswear">Type</span>
                    <span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span>
                  </div>
                  {gstInfo.address && (
                    <div className="gst-info-row">
                      <span className="gst-label sportswear">Address</span>
                      <span className="gst-value small">{gstInfo.address}</span>
                    </div>
                  )}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="sp-company">Company Name <span className="req">*</span></label>
                <input type="text" id="sp-company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="sp-email">Email <span className="req">*</span></label>
                <input type="email" id="sp-email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <button type="submit" className="submit-btn sportswear" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Free Samples"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples · Cash or credit options · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon sportswear-icon">✓</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our sportswear fabric experts will contact you within 24 hours with sample options and best rates.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn sportswear-btn">Browse Sportswear Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip sp-stats">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Sample<br/>Delivery</div></div>
          <div className="stat"><div className="stat-val">160–260</div><div className="stat-lbl">GSM<br/>Range</div></div>
        </div>

        <section className="section">
          <div className="section-label sp-text">Fabric Catalogue</div>
          <div className="section-title">Polyester knit fabrics<br/>for every sportswear need.</div>
          <div className="vp-list">
            {fabricTypes.map((fab, i) => (
              <div key={i} className="vp-card sp-highlight">
                <div className="vp-num sp-num">{fab.name}</div>
                <div>
                  <div className="vp-title">{fab.spec} · {fab.gsm}</div>
                  <div className="vp-desc">{fab.use} <span className="free-tag">FREE SAMPLE</span></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label sp-text">Why Locofast</div>
          <div className="section-title">Source smarter.<br/>Grow faster.</div>
          <div className="vp-list">
            {[
              { title: "Direct Mill Pricing", desc: "No middlemen. Get the best rates directly from polyester knit mills across Surat, Ludhiana and Tiruppur." },
              { title: "Free Samples Delivered", desc: "Request fabric swatches from any mill at zero cost. Feel the quality before you commit." },
              { title: "Cash or Credit — Your Choice", desc: "Pay upfront for better rates or use flexible 30/60/90-day payment terms. No collateral needed." },
              { title: "Quality Guaranteed", desc: "Every mill is verified. Every order is quality-checked before dispatch. Your money is protected until you approve." }
            ].map((vp, i) => (
              <div key={i} className="vp-card">
                <div className="vp-num">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label sp-text">How It Works</div>
          <div className="section-title">From inquiry to<br/>delivery in 4 steps.</div>
          <div className="steps">
            {[
              { title: "Submit Your Requirements", desc: "Tell us your fabric type, GSM, quantity and delivery timeline." },
              { title: "Get Matched with Mills", desc: "We connect you with the best polyester knit mills for your sportswear needs." },
              { title: "Receive Free Samples", desc: "Get physical swatches delivered to your doorstep at no cost." },
              { title: "Place Your Order", desc: "Finalize pricing and place your order with cash or credit payment options." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle sp-circle">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};


// India Rayon Landing Page Component
const IndiaRayonLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    gstNumber: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const fabricTypes = [
    { name: "30s Rayon Greige", spec: "68 x 52 | 63\"", price: "36.70/mtr", use: "Kurtis, Ethnic Wear" },
    { name: "40s Rayon Plain", spec: "60 x 60 | 58\"", price: "42.50/mtr", use: "Dresses, Tops" },
    { name: "30s Rayon Twill", spec: "68 x 52 | 58\"", price: "39.80/mtr", use: "Palazzos, Bottoms" },
    { name: "60s Rayon Modal", spec: "80 x 64 | 44\"", price: "55.00/mtr", use: "Premium Wear, Drapes" }
  ];

  const industries = [
    "Kurti Manufacturers", "Ethnic Wear Brands", "Fabric Traders", "Garment Exporters",
    "Fashion Labels", "Women's Wear Makers", "Home Textile Units", "Wholesale Dealers",
    "Dress Material Sellers", "Boutique Owners"
  ];

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV"); return; }

    setGstLoading(true);
    setGstError("");
    setGstInfo(null);

    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        setGstInfo(response.data.data);
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else {
        setGstError(response.data.error || "GST lookup failed");
      }
    } catch (error) {
      setGstError("Failed to fetch GST details. Please try again.");
    } finally {
      setGstLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid business email.";
    if (!formData.phone.trim() || formData.phone.length !== 10) newErrors.phone = "Please enter a valid 10-digit number.";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "Please enter your GST number.";
    if (formData.gstNumber && !validateGST(formData.gstNumber.toUpperCase())) newErrors.gstNumber = "Invalid GST format.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: "rayon_buyer",
        phone: `+91${formData.phone}`,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-rayon"
      });
      setIsSuccess(true);
    } catch (error) {
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page india-rayon">
      <div className="fold rayon-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-ry">RAYON FABRIC</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Rayon Fabric at<br/><em>Best Mill Prices</em></h1>
          <p className="hero-sub">48 hours delivery. Cash or credit. Direct from verified mills.</p>
          <p className="hero-sub-hindi">Rayon फैब्रिक अब और भी सस्ते दामों में · 48 घंटे की डिलीवरी</p>
          
          <div className="denim-chips">
            <span className="denim-chip ry-chip">Greige</span>
            <span className="denim-chip ry-chip">Plain Dyed</span>
            <span className="denim-chip ry-chip">Twill</span>
            <span className="denim-chip ry-chip">Modal Blend</span>
          </div>
          <div className="denim-chips composition-chips">
            <span className="denim-chip comp">Cash or Credit</span>
            <span className="denim-chip comp">500+ Mills</span>
            <span className="denim-chip comp">48hr Delivery</span>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Trusted by India's top rayon buyers & D2C brands</div>
          <div className="marquee-track">
            {["Kotty", "Saadaa", "The Loom", "Here & Now (Myntra)", "Salona Bichona", "NYPA (Myntra)", "Kizi Apparels", "FableStreet", "Lamba Creations", "SHIONS (Myntra)", "Maaesa (Myntra)", "UNSTD (Myntra)", "Kotty", "Saadaa", "The Loom", "Here & Now (Myntra)", "Salona Bichona", "NYPA (Myntra)", "Kizi Apparels", "FableStreet", "Lamba Creations", "SHIONS (Myntra)", "Maaesa (Myntra)", "UNSTD (Myntra)"].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Best Rates & Free Samples</div>
            <div className="form-sub">Tell us your rayon fabric requirements — our experts will reach out within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="ry-name">Name <span className="req">*</span></label>
                <input type="text" id="ry-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="ry-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="ry-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="ry-gst">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="ry-gst" data-testid="gst-input" placeholder="27AAPFU0939F1ZV" maxLength="15"
                    value={formData.gstNumber}
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn rayon" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card rayon" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>
                      {gstInfo.status}
                    </span>
                    <span className="gst-verified rayon">GST Verified</span>
                  </div>
                  <div className="gst-info-row">
                    <span className="gst-label rayon">Legal Name</span>
                    <span className="gst-value">{gstInfo.legal_name || '-'}</span>
                  </div>
                  {gstInfo.trade_name && (
                    <div className="gst-info-row">
                      <span className="gst-label rayon">Trade Name</span>
                      <span className="gst-value">{gstInfo.trade_name}</span>
                    </div>
                  )}
                  <div className="gst-info-row">
                    <span className="gst-label rayon">Type</span>
                    <span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span>
                  </div>
                  {gstInfo.address && (
                    <div className="gst-info-row">
                      <span className="gst-label rayon">Address</span>
                      <span className="gst-value small">{gstInfo.address}</span>
                    </div>
                  )}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="ry-company">Company Name <span className="req">*</span></label>
                <input type="text" id="ry-company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="ry-email">Email <span className="req">*</span></label>
                <input type="email" id="ry-email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <button type="submit" className="submit-btn rayon-btn" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Best Rates"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples · Cash or credit · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon rayon-icon">✓</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our rayon fabric experts will contact you within 24 hours with best rates and sample options.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn rayon-shop">Browse Rayon Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip ry-stats">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Delivery<br/>Time</div></div>
          <div className="stat"><div className="stat-val">30-90</div><div className="stat-lbl">Day Credit<br/>Terms</div></div>
        </div>

        <section className="section">
          <div className="section-label ry-text">Rayon Catalogue</div>
          <div className="section-title">Best mill-gate prices.<br/>Direct to your factory.</div>
          <p className="section-subtitle">सबसे सस्ते दामों में Rayon फैब्रिक · सीधे मिल से</p>
          <div className="vp-list">
            {fabricTypes.map((fab, i) => (
              <div key={i} className="vp-card ry-highlight">
                <div className="vp-num ry-num">{fab.name}</div>
                <div>
                  <div className="vp-title">{fab.spec} · {fab.use}</div>
                  <div className="vp-desc"><span className="ry-price">From ₹{fab.price}</span> <span className="free-tag">FREE SAMPLE</span></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label ry-text">Why Locofast</div>
          <div className="section-title">Cash or Credit,<br/>your choice.</div>
          <p className="section-subtitle">Cash या Credit, अपकी पसंद · कोई बिचौलिया नहीं</p>
          <div className="vp-list">
            {[
              { title: "Direct Mill Pricing", desc: "No middlemen. Get the best rates directly from rayon mills across Surat, Bhilwara and Jetpur." },
              { title: "48-Hour Delivery", desc: "Fast dispatch from mills nearest to you. Track your shipment in real-time." },
              { title: "Cash or Credit Options", desc: "Pay upfront for the best rates or use 30/60/90-day flexible payment terms. No collateral." },
              { title: "Free Samples", desc: "Request fabric swatches from any mill at zero cost before placing your bulk order." }
            ].map((vp, i) => (
              <div key={i} className="vp-card">
                <div className="vp-num">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label ry-text">How It Works</div>
          <div className="section-title">From inquiry to<br/>delivery in 4 steps.</div>
          <div className="steps">
            {[
              { title: "Submit Your Requirements", desc: "Tell us your rayon fabric specs — count, construction, width and quantity." },
              { title: "Get Best Rates from Mills", desc: "We source quotes from the best rayon mills for your specific requirements." },
              { title: "Receive Free Samples", desc: "Get physical swatches delivered to your doorstep at no cost." },
              { title: "Place Your Order", desc: "Finalize pricing, choose cash or credit, and get delivery in 48 hours." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle ry-circle">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};


// Sri Lanka Generic Landing Page Component
const SriLankaLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    companyType: "",
    email: "",
    website: "",
    fabricCategories: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const fabricOptions = [
    { value: "denim", label: "Denim" },
    { value: "cotton_wovens", label: "Cotton Wovens" },
    { value: "knits_sportswear", label: "Knits & Sportswear" },
    { value: "other", label: "Other" }
  ];

  const toggleFabric = (value) => {
    setFormData(prev => ({
      ...prev,
      fabricCategories: prev.fabricCategories.includes(value)
        ? prev.fabricCategories.filter(v => v !== value)
        : [...prev.fabricCategories, value]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.companyType) newErrors.companyType = "Please select your company type.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Please use your official company email (not Gmail, Yahoo, etc.)";
    }
    if (!formData.website.trim()) {
      newErrors.website = "Please enter your company website.";
    } else if (!/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.website)) {
      newErrors.website = "Please enter a valid website URL.";
    }
    if (formData.fabricCategories.length === 0) newErrors.fabricCategories = "Please select at least one fabric category.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: formData.companyType,
        phone: `+94${formData.phone}`,
        website: formData.website,
        fabric_type: formData.fabricCategories.join(", "),
        campaign: "srilanka-generic"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page srilanka-page">
      <div className="fold srilanka-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-sl">NOW IN SRI LANKA</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Sourcing Fabric<br/>from India?<br/><em>You're Overpaying.</em></h1>
          <p className="hero-sub">Most Sri Lanka factories pay 15-20% more than they should because they're going through agents. We're changing that.</p>

          <div className="sl-problem-card" data-testid="problem-card">
            <div className="sl-problem-icon">!</div>
            <div className="sl-problem-text">
              <strong>The agent markup problem:</strong> Agents add 15-20% to your fabric cost. With Locofast, you source directly from 500+ verified Indian mills at real factory-gate prices. No middlemen. No markups.
            </div>
          </div>
          
          <div className="denim-chips">
            <span className="denim-chip sl-chip">500+ Indian Mills</span>
            <span className="denim-chip sl-chip">Direct Pricing</span>
            <span className="denim-chip sl-chip">No Agent Fees</span>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Supplying fabrics for India & Bangladesh's biggest brands & factories</div>
          <div className="marquee-track">
            {["Myntra Private Labels", "D2C Brands", "Flipkart Brands", "Meesho Sellers", "Reliance Manufacturers", "Westside Manufacturers", "Largest Bangladesh Factories", "Ajio Brand Partners", "Amazon Sellers", "Nykaa Fashion Labels", "Export Houses", "Myntra Private Labels", "D2C Brands", "Flipkart Brands", "Meesho Sellers", "Reliance Manufacturers"].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Direct Mill Prices</div>
            <div className="form-sub">Tell us what you source — our team will show you how much you can save by going direct.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="sl-name">Name <span className="req">*</span></label>
                <input type="text" id="sl-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="sl-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+94</div>
                  <input type="tel" id="sl-phone" data-testid="phone-input" placeholder="7XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="sl-company">Company Name <span className="req">*</span></label>
                <input type="text" id="sl-company" data-testid="company-input" placeholder="Your company name"
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                {errors.company && <div className="field-error">{errors.company}</div>}
              </div>

              <div className={`field ${errors.companyType ? "error" : ""}`}>
                <label htmlFor="sl-companyType">Company Type <span className="req">*</span></label>
                <div className="select-wrapper">
                  <select id="sl-companyType" data-testid="company-type-select" value={formData.companyType}
                    onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}>
                    <option value="" disabled>Select your company type</option>
                    <option value="garment_manufacturer">Garment Manufacturer</option>
                    <option value="buying_house">Buying House</option>
                    <option value="fabric_wholesaler">Fabric Wholesaler / Trader</option>
                    <option value="brand">Brand / Designer</option>
                    <option value="export_house">Export House</option>
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                {errors.companyType && <div className="field-error">{errors.companyType}</div>}
              </div>

              <div className={`field ${errors.fabricCategories ? "error" : ""}`}>
                <label>What fabric categories do you source? <span className="req">*</span></label>
                <div className="sl-fabric-grid" data-testid="fabric-categories">
                  {fabricOptions.map(opt => (
                    <button key={opt.value} type="button"
                      className={`sl-fabric-chip ${formData.fabricCategories.includes(opt.value) ? "active" : ""}`}
                      data-testid={`fabric-chip-${opt.value}`}
                      onClick={() => toggleFabric(opt.value)}>
                      {formData.fabricCategories.includes(opt.value) && <span className="sl-chip-check">✓</span>}
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="field-hint">Select all that apply</div>
                {errors.fabricCategories && <div className="field-error">{errors.fabricCategories}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="sl-email">Official Email ID <span className="req">*</span></label>
                <input type="email" id="sl-email" data-testid="email-input" placeholder="you@yourcompany.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <div className="field-hint">Use your company email (Gmail, Yahoo not accepted)</div>
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.website ? "error" : ""}`}>
                <label htmlFor="sl-website">Company Website <span className="req">*</span></label>
                <input type="text" id="sl-website" data-testid="website-input" placeholder="www.yourcompany.com"
                  value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                {errors.website && <div className="field-error">{errors.website}</div>}
              </div>

              <button type="submit" className="submit-btn srilanka" data-testid="submit-btn" disabled={isSubmitting}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "See How Much You Can Save"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                No commitment · Free consultation · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon srilanka-icon">✓</div>
            <div className="success-title">We've Got Your Details!</div>
            <div className="success-sub">Our Sri Lanka market team will reach out within 48 hours with a custom pricing comparison showing exactly how much you can save by sourcing direct.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn srilanka-shop">Explore Locofast Platform →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip sl-stats">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified Indian<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">15-20%</div><div className="stat-lbl">Avg. Savings<br/>vs Agents</div></div>
          <div className="stat"><div className="stat-val">2</div><div className="stat-lbl">Countries<br/>Already Served</div></div>
        </div>

        <section className="section">
          <div className="section-label sl-text">The Problem</div>
          <div className="section-title">Why Sri Lanka factories<br/>pay more for Indian fabric.</div>
          <div className="vp-list">
            {[
              { title: "Agent Markups of 15-20%", desc: "Most Sri Lanka factories source Indian fabric through agents who add layers of cost. You never see the real mill price.", icon: "1" },
              { title: "Zero Price Transparency", desc: "Without direct mill access, you can't compare rates. Agents control information and you can't negotiate effectively.", icon: "2" },
              { title: "Quality Surprises on Arrival", desc: "When agents manage the order, quality checks happen at their discretion. Issues surface only after the fabric reaches you.", icon: "3" }
            ].map((item, i) => (
              <div key={i} className="vp-card sl-problem-highlight">
                <div className="vp-num sl-problem-num">{item.icon}</div>
                <div><div className="vp-title">{item.title}</div><div className="vp-desc">{item.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label sl-text">The Locofast Advantage</div>
          <div className="section-title">Already powering India's top brands<br/>& Bangladesh's largest factories.</div>
          <p className="section-subtitle">Manufacturers supplying Myntra, Flipkart, Meesho, Reliance, Westside, and 500+ Bangladesh garment factories already source through Locofast. Now we're bringing the same advantage to Sri Lanka.</p>
          <div className="vp-list">
            {[
              { title: "Direct Mill-Gate Pricing", desc: "Source from 500+ verified mills across India — Surat, Ahmedabad, Tiruppur, Ludhiana. No agents, no markups. The same mills that supply Myntra and Flipkart private labels." },
              { title: "Every Fabric Category", desc: "Denim, cotton wovens, polyester knits, rayon, linen — one platform for all your sourcing needs. Compare rates across mills instantly." },
              { title: "Payment Protection Built In", desc: "Your money is held in escrow until you receive and approve the fabric. Zero risk of fraud or quality disputes." },
              { title: "Free Samples Before You Commit", desc: "Request physical fabric swatches from any mill at no cost. Feel the quality before placing your bulk order." },
              { title: "Cash or Credit — Your Choice", desc: "Pay upfront for the best rates, or use 30/60/90-day flexible payment terms. No collateral needed." },
              { title: "Dedicated Sri Lanka Support", desc: "A local market specialist helps you navigate Indian mills, negotiate rates, and manage your orders end to end." }
            ].map((vp, i) => (
              <div key={i} className={`vp-card ${i < 3 ? "sl-highlight" : ""}`}>
                <div className={`vp-num ${i < 3 ? "sl-num" : ""}`}>{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label sl-text">Trusted By</div>
          <div className="section-title">The brands & factories<br/>already on Locofast.</div>
          <p className="section-subtitle">From India's biggest e-commerce brands to Bangladesh's largest garment exporters.</p>
          <div className="marquee-wrap sl-marquee-section">
            <div className="marquee-track">
              {["Myntra Private Labels", "Flipkart Brands", "Meesho Sellers", "Reliance Manufacturers", "Westside Manufacturers", "Ajio Brand Partners", "D2C Fashion Brands", "Bangladesh Top Factories", "Nykaa Fashion Labels", "Amazon India Sellers", "Shoppers Stop Vendors", "Global Export Houses", "Myntra Private Labels", "Flipkart Brands", "Meesho Sellers", "Reliance Manufacturers", "Westside Manufacturers", "Ajio Brand Partners"].map((name, i) => (
                <div key={i} className="m-chip sl-brand-chip">{name}</div>
              ))}
            </div>
          </div>
          <div className="sl-trusted-footer">+ 500 more factories across India & Bangladesh</div>
        </section>

        <section className="section">
          <div className="section-label sl-text">How It Works</div>
          <div className="section-title">From your first inquiry to<br/>fabric at your factory.</div>
          <div className="steps">
            {[
              { title: "Tell Us What You Source", desc: "Share your fabric requirements — type, specs, volume, and current pricing. We'll show you the savings." },
              { title: "Get a Custom Price Comparison", desc: "Our team sends you a side-by-side comparison: your current agent price vs. direct mill price." },
              { title: "Request Free Samples", desc: "Once you see the savings, request physical swatches from any mill. Delivered to Colombo at no cost." },
              { title: "Place Your First Order", desc: "Order with full payment protection. Track shipment from the Indian mill to your factory door." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle sl-circle">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label sl-text">Fabric Categories</div>
          <div className="section-title">One platform for<br/>every fabric you need.</div>
          <div className="sl-category-grid">
            {[
              { name: "Denim", desc: "Indigo, stretch, raw, black — every weight from 6oz to 14oz." },
              { name: "Cotton Wovens", desc: "Poplin, twill, oxford, voile — for shirts, dresses, and uniforms." },
              { name: "Knits & Sportswear", desc: "Polyester lycra, jersey, pique — for activewear and athleisure." },
              { name: "Rayon & Blends", desc: "Viscose, modal, rayon twill — for ethnic wear and casual fashion." }
            ].map((cat, i) => (
              <div key={i} className="sl-category-card">
                <div className="sl-category-name">{cat.name}</div>
                <div className="sl-category-desc">{cat.desc}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// Vietnam Generic Landing Page Component
const VietnamLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    companyType: "",
    email: "",
    website: "",
    fabricCategories: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const fabricOptions = [
    { value: "denim", label: "Denim" },
    { value: "cotton_wovens", label: "Cotton Wovens" },
    { value: "knits_sportswear", label: "Knits & Sportswear" },
    { value: "other", label: "Other" }
  ];

  const toggleFabric = (value) => {
    setFormData(prev => ({
      ...prev,
      fabricCategories: prev.fabricCategories.includes(value)
        ? prev.fabricCategories.filter(v => v !== value)
        : [...prev.fabricCategories, value]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.companyType) newErrors.companyType = "Please select your company type.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    } else if (!isOfficialEmail(formData.email)) {
      newErrors.email = "Please use your official company email (not Gmail, Yahoo, etc.)";
    }
    if (!formData.website.trim()) {
      newErrors.website = "Please enter your company website.";
    } else if (!/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.website)) {
      newErrors.website = "Please enter a valid website URL.";
    }
    if (formData.fabricCategories.length === 0) newErrors.fabricCategories = "Please select at least one fabric category.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: formData.companyType,
        phone: `+84${formData.phone}`,
        website: formData.website,
        fabric_type: formData.fabricCategories.join(", "),
        campaign: "vietnam-generic"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page vietnam-page">
      <div className="fold vietnam-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-vn">NOW IN VIETNAM</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Sourcing Fabric<br/>from India?<br/><em>You're Overpaying.</em></h1>
          <p className="hero-sub">Most Vietnam factories pay 15-20% more than they should because they're going through agents. We're changing that.</p>

          <div className="sl-problem-card" data-testid="problem-card">
            <div className="sl-problem-icon">!</div>
            <div className="sl-problem-text">
              <strong>The agent markup problem:</strong> Agents add 15-20% to your fabric cost. With Locofast, you source directly from 500+ verified Indian mills at real factory-gate prices. No middlemen. No markups.
            </div>
          </div>
          
          <div className="denim-chips">
            <span className="denim-chip vn-chip">500+ Indian Mills</span>
            <span className="denim-chip vn-chip">Direct Pricing</span>
            <span className="denim-chip vn-chip">No Agent Fees</span>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Supplying fabrics for the world's biggest brands</div>
          <div className="marquee-track">
            {[...GLOBAL_BRAND_TICKER, ...GLOBAL_BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Direct Mill Prices</div>
            <div className="form-sub">Tell us what you source — our team will show you how much you can save by going direct.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="vn-name">Name <span className="req">*</span></label>
                <input type="text" id="vn-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="vn-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+84</div>
                  <input type="tel" id="vn-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="vn-company">Company Name <span className="req">*</span></label>
                <input type="text" id="vn-company" data-testid="company-input" placeholder="Your company name"
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                {errors.company && <div className="field-error">{errors.company}</div>}
              </div>

              <div className={`field ${errors.companyType ? "error" : ""}`}>
                <label htmlFor="vn-companyType">Company Type <span className="req">*</span></label>
                <div className="select-wrapper">
                  <select id="vn-companyType" data-testid="company-type-select" value={formData.companyType}
                    onChange={(e) => setFormData({ ...formData, companyType: e.target.value })}>
                    <option value="" disabled>Select your company type</option>
                    <option value="garment_manufacturer">Garment Manufacturer</option>
                    <option value="buying_house">Buying House</option>
                    <option value="fabric_wholesaler">Fabric Wholesaler / Trader</option>
                    <option value="brand">Brand / Designer</option>
                    <option value="export_house">Export House</option>
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                {errors.companyType && <div className="field-error">{errors.companyType}</div>}
              </div>

              <div className={`field ${errors.fabricCategories ? "error" : ""}`}>
                <label>What fabric categories do you source? <span className="req">*</span></label>
                <div className="sl-fabric-grid" data-testid="fabric-categories">
                  {fabricOptions.map(opt => (
                    <button key={opt.value} type="button"
                      className={`sl-fabric-chip ${formData.fabricCategories.includes(opt.value) ? "active" : ""}`}
                      data-testid={`fabric-chip-${opt.value}`}
                      onClick={() => toggleFabric(opt.value)}>
                      {formData.fabricCategories.includes(opt.value) && <span className="sl-chip-check">✓</span>}
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="field-hint">Select all that apply</div>
                {errors.fabricCategories && <div className="field-error">{errors.fabricCategories}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="vn-email">Official Email ID <span className="req">*</span></label>
                <input type="email" id="vn-email" data-testid="email-input" placeholder="you@yourcompany.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <div className="field-hint">Use your company email (Gmail, Yahoo not accepted)</div>
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.website ? "error" : ""}`}>
                <label htmlFor="vn-website">Company Website <span className="req">*</span></label>
                <input type="text" id="vn-website" data-testid="website-input" placeholder="www.yourcompany.com"
                  value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                {errors.website && <div className="field-error">{errors.website}</div>}
              </div>

              <button type="submit" className="submit-btn vietnam" data-testid="submit-btn" disabled={isSubmitting}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "See How Much You Can Save"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                No commitment · Free consultation · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon vietnam-icon">✓</div>
            <div className="success-title">We've Got Your Details!</div>
            <div className="success-sub">Our Vietnam market team will reach out within 48 hours with a custom pricing comparison showing exactly how much you can save by sourcing direct.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn vietnam-shop">Explore Locofast Platform →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip vn-stats">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified Indian<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">15-20%</div><div className="stat-lbl">Avg. Savings<br/>vs Agents</div></div>
          <div className="stat"><div className="stat-val">3</div><div className="stat-lbl">Countries<br/>Already Served</div></div>
        </div>

        <section className="section">
          <div className="section-label vn-text">The Problem</div>
          <div className="section-title">Why Vietnam factories<br/>pay more for Indian fabric.</div>
          <div className="vp-list">
            {[
              { title: "Agent Markups of 15-20%", desc: "Most Vietnam factories source Indian fabric through agents who add layers of cost. You never see the real mill price.", icon: "1" },
              { title: "Zero Price Transparency", desc: "Without direct mill access, you can't compare rates. Agents control information and you can't negotiate effectively.", icon: "2" },
              { title: "Quality Surprises on Arrival", desc: "When agents manage the order, quality checks happen at their discretion. Issues surface only after the fabric reaches you.", icon: "3" }
            ].map((item, i) => (
              <div key={i} className="vp-card vn-problem-highlight">
                <div className="vp-num vn-problem-num">{item.icon}</div>
                <div><div className="vp-title">{item.title}</div><div className="vp-desc">{item.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label vn-text">The Locofast Advantage</div>
          <div className="section-title">Already powering India's top brands,<br/>Bangladesh & Sri Lanka's factories.</div>
          <p className="section-subtitle">Manufacturers supplying Walmart, H&M, Target, Primark, and thousands of factories across 3 countries already source through Locofast. Now we're bringing the same advantage to Vietnam.</p>
          <div className="marquee-wrap vn-marquee-section">
            <div className="marquee-track">
              {[...GLOBAL_BRAND_TICKER, ...GLOBAL_BRAND_TICKER].map((name, i) => (
                <div key={i} className="m-chip sl-brand-chip">{name}</div>
              ))}
            </div>
          </div>
          <div className="vp-list" style={{marginTop: "24px"}}>
            {[
              { title: "Direct Mill-Gate Pricing", desc: "Source from 500+ verified mills across India — Surat, Ahmedabad, Tiruppur, Ludhiana. No agents, no markups. The same mills supplying Walmart and H&M manufacturers." },
              { title: "Every Fabric Category", desc: "Denim, cotton wovens, polyester knits, rayon, linen — one platform for all your sourcing needs. Compare rates across mills instantly." },
              { title: "Payment Protection Built In", desc: "Your money is held in escrow until you receive and approve the fabric. Zero risk of fraud or quality disputes." },
              { title: "Free Samples Before You Commit", desc: "Request physical fabric swatches from any mill at no cost. Feel the quality before placing your bulk order." },
              { title: "Cash or Credit — Your Choice", desc: "Pay upfront for the best rates, or use 30/60/90-day flexible payment terms. No collateral needed." },
              { title: "Dedicated Vietnam Support", desc: "A local market specialist helps you navigate Indian mills, negotiate rates, and manage your orders end to end." }
            ].map((vp, i) => (
              <div key={i} className={`vp-card ${i < 3 ? "vn-highlight" : ""}`}>
                <div className={`vp-num ${i < 3 ? "vn-num" : ""}`}>{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label vn-text">How It Works</div>
          <div className="section-title">From your first inquiry to<br/>fabric at your factory.</div>
          <div className="steps">
            {[
              { title: "Tell Us What You Source", desc: "Share your fabric requirements — type, specs, volume, and current pricing. We'll show you the savings." },
              { title: "Get a Custom Price Comparison", desc: "Our team sends you a side-by-side comparison: your current agent price vs. direct mill price." },
              { title: "Request Free Samples", desc: "Once you see the savings, request physical swatches from any mill. Delivered to Ho Chi Minh City or Hanoi at no cost." },
              { title: "Place Your First Order", desc: "Order with full payment protection. Track shipment from the Indian mill to your factory door." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle vn-circle">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label vn-text">Fabric Categories</div>
          <div className="section-title">One platform for<br/>every fabric you need.</div>
          <div className="sl-category-grid">
            {[
              { name: "Denim", desc: "Indigo, stretch, raw, black — every weight from 6oz to 14oz." },
              { name: "Cotton Wovens", desc: "Poplin, twill, oxford, voile — for shirts, dresses, and uniforms." },
              { name: "Knits & Sportswear", desc: "Polyester lycra, jersey, pique — for activewear and athleisure." },
              { name: "Rayon & Blends", desc: "Viscose, modal, rayon twill — for ethnic wear and casual fashion." }
            ].map((cat, i) => (
              <div key={i} className="sl-category-card">
                <div className="sl-category-name">{cat.name}</div>
                <div className="sl-category-desc">{cat.desc}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};



// India Men Shirt Landing Page Component
const IndiaShirtLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    gstNumber: "",
    email: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  // Fire Lead pixel on thank you page
  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const shirtTypes = [
    "Formal Shirts", "Casual Shirts", "Dress Shirts", "Oxford Shirts",
    "Linen Shirts", "Denim Shirts", "Polo Shirts", "Printed Shirts"
  ];

  const fabrics = [
    "100% Cotton", "Cotton Blend", "Linen", "Chambray"
  ];

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) {
      setGstError("Please enter a GST number");
      return;
    }
    
    if (!validateGST(formData.gstNumber.toUpperCase())) {
      setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV");
      return;
    }

    setGstLoading(true);
    setGstError("");
    setGstInfo(null);

    try {
      const response = await axios.post(`${API}/gst/lookup`, {
        gstin: formData.gstNumber.toUpperCase()
      });

      if (response.data.success) {
        setGstInfo(response.data.data);
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else {
        setGstError(response.data.error || "GST lookup failed");
      }
    } catch (error) {
      console.error("GST lookup error:", error);
      setGstError("Failed to fetch GST details. Please try again.");
    } finally {
      setGstLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim() || formData.phone.length !== 10) newErrors.phone = "Please enter a valid 10-digit phone number.";
    if (!formData.company.trim()) newErrors.company = "Please verify GST to auto-fill company name.";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "Please enter your GST number.";
    if (formData.gstNumber && !validateGST(formData.gstNumber.toUpperCase())) {
      newErrors.gstNumber = "Invalid GST format.";
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: "shirt_buyer",
        phone: `+91${formData.phone}`,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-shirt"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page india-shirt-page">
      <div className="fold india-shirt-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <svg viewBox="0 0 16 16"><path d="M8 1l1.545 3.09L13 4.636l-2.5 2.436.59 3.428L8 8.91l-3.09 1.59.59-3.428L3 4.636l3.455-.546L8 1z" fill="white" opacity="0.9"/></svg>
            <span>India's Premium Shirting Fabric Platform</span>
          </div>
          <h1>Source Premium<br/><em>Men's Shirt Fabrics</em><br/>Direct from Mills</h1>
          <p className="hero-sub">Access 500+ verified mills for shirting fabrics.<br/>Best rates, free samples & flexible payment terms.</p>
          
          <div className="denim-chips">
            {shirtTypes.slice(0, 4).map((type, i) => (
              <span key={i} className="denim-chip">{type}</span>
            ))}
          </div>
          <div className="denim-chips composition-chips">
            {fabrics.map((fab, i) => (
              <span key={i} className="denim-chip comp">{fab}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Supplying shirting fabrics for India's top brands</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Best Rates & Samples</div>
            <div className="form-sub">Fill in your details — our shirting experts will reach out within 24 hours.</div>
            
            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="name">Name <span className="req">*</span></label>
                <input type="text" id="name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="gstNumber">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="gstNumber" data-testid="gst-input" placeholder="27AAPFU0939F1ZV"
                    maxLength="15"
                    value={formData.gstNumber} 
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn india-shirt" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card india-shirt" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>
                      {gstInfo.status}
                    </span>
                    <span className="gst-verified india-shirt">GST Verified</span>
                  </div>
                  <div className="gst-info-row">
                    <span className="gst-label india-shirt">Legal Name</span>
                    <span className="gst-value">{gstInfo.legal_name || '-'}</span>
                  </div>
                  {gstInfo.trade_name && (
                    <div className="gst-info-row">
                      <span className="gst-label india-shirt">Trade Name</span>
                      <span className="gst-value">{gstInfo.trade_name}</span>
                    </div>
                  )}
                  <div className="gst-info-row">
                    <span className="gst-label india-shirt">Type</span>
                    <span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span>
                  </div>
                  {gstInfo.address && (
                    <div className="gst-info-row">
                      <span className="gst-label india-shirt">Address</span>
                      <span className="gst-value small">{gstInfo.address}</span>
                    </div>
                  )}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="company">Company Name <span className="req">*</span></label>
                <input type="text" id="company" data-testid="company-input" placeholder="← Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">Email ID <span className="req">*</span></label>
                <input type="email" id="email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <button type="submit" className="submit-btn india-shirt" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Best Rates & Samples"}</span>
                {!isSubmitting && <span className="btn-arrow">→</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples · No commitment · <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon india-shirt">✓</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our shirting experts will contact you within 24 hours with sample options and best rates.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn india-shirt">Browse Shirting Fabrics →</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">1000+</div><div className="stat-lbl">Fabric<br/>Options</div></div>
          <div className="stat"><div className="stat-val">48 hrs</div><div className="stat-lbl">Sample<br/>Delivery</div></div>
        </div>

        <section className="section">
          <div className="section-label india-shirt-text">Shirting Range</div>
          <div className="section-title">Every shirting fabric<br/>you need.</div>
          <div className="vp-list">
            {[
              { title: "Premium Cotton Shirting", desc: "60s, 80s, 100s count cotton in poplin, twill, and dobby weaves for formal shirts." },
              { title: "Oxford & Chambray", desc: "Classic oxford cloth and chambray fabrics for casual and semi-formal shirts." },
              { title: "Linen & Linen Blends", desc: "Pure linen and cotton-linen blends for summer collections and resort wear." },
              { title: "Printed & Yarn Dyed", desc: "Checks, stripes, and prints for trendy casual shirt collections." },
              { title: "Stretch Shirting", desc: "Cotton-spandex blends for comfort fit formal and casual shirts." },
              { title: "Sustainable Options", desc: "Organic cotton, BCI certified, and recycled fabric options available." }
            ].map((vp, i) => (
              <div key={i} className="vp-card india-shirt-highlight">
                <div className="vp-num india-shirt">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label india-shirt-text">How It Works</div>
          <div className="section-title">From inquiry to<br/>delivery.</div>
          <div className="steps">
            {[
              { title: "Submit Your Requirements", desc: "Tell us your shirting specs — fabric type, count, weave, quantity." },
              { title: "Get Matched with Mills", desc: "We connect you with the best mills for your specific needs." },
              { title: "Receive Free Samples", desc: "Get physical swatches delivered to your doorstep at no cost." },
              { title: "Place Bulk Order", desc: "Finalize pricing and place your order with payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle india-shirt">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// INDIA DENIM SELLER ACQUISITION CAMPAIGN
// ─────────────────────────────────────────────────────────────
const DenimSellerLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", company: "", gstNumber: "", email: "",
    city: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) {
      window.fbq('track', 'Lead');
    }
  }, [isSuccess]);

  const denimCategories = [
    "Raw Denim", "Stretch Denim", "Selvedge Denim", "Organic Denim",
    "Recycled Denim", "Coated Denim", "Printed Denim", "Chambray", "Bull Denim"
  ];

  const capacityOptions = [
    "Up to 50,000 mtrs/month",
    "50,000 - 1,00,000 mtrs/month",
    "1,00,000 - 5,00,000 mtrs/month",
    "5,00,000+ mtrs/month"
  ];

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const lookupGST = async () => {
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV"); return; }
    setGstLoading(true); setGstError(""); setGstInfo(null);
    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        setGstInfo(response.data.data);
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else { setGstError(response.data.error || "GST lookup failed"); }
    } catch (error) { setGstError("Failed to fetch GST details. Please try again."); }
    finally { setGstLoading(false); }
  };

  const toggleDenimType = (type) => {
    setFormData(prev => ({
      ...prev,
      denimTypes: prev.denimTypes.includes(type)
        ? prev.denimTypes.filter(t => t !== type)
        : [...prev.denimTypes, type]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim() || formData.phone.length !== 10) newErrors.phone = "Please enter a valid 10-digit phone number.";
    if (!formData.company.trim()) newErrors.company = "Please enter your company name.";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "Please enter your GST number.";
    if (formData.gstNumber && !validateGST(formData.gstNumber.toUpperCase())) newErrors.gstNumber = "Invalid GST format.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email.";
    if (!formData.city.trim()) newErrors.city = "Please enter your city.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        company_type: "denim_seller",
        phone: `+91${formData.phone}`,
        city: formData.city,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-denim-seller"
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting lead:", error);
      alert("Something went wrong. Please try again.");
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="landing-page denim-seller-page">
      <div className="fold denim-seller-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <svg viewBox="0 0 16 16"><path d="M8 1l1.545 3.09L13 4.636l-2.5 2.436.59 3.428L8 8.91l-3.09 1.59.59-3.428L3 4.636l3.455-.546L8 1z" fill="white" opacity="0.9"/></svg>
            <span>India's Largest B2B Denim Marketplace</span>
          </div>
          <h1>Sell Your Denim on<br/><em>India's Largest</em><br/>B2B Fabric Platform</h1>
          <p className="hero-sub">Get access to 500+ verified buyers — fashion brands, garment manufacturers & export houses. Zero commission to list.</p>

          <div className="denim-chips">
            {["500+ Active Buyers", "Zero Listing Fee", "Secure Payments", "PAN India"].map((tag, i) => (
              <span key={i} className="denim-chip">{tag}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Denim types in demand</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Register as a Denim Seller</div>
            <div className="form-sub">Join 100+ mills already selling on Locofast. Our team will onboard you within 48 hours.</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="name">Contact Name <span className="req">*</span></label>
                <input type="text" id="name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="gstNumber">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="gstNumber" data-testid="gst-input" placeholder="27AAPFU0939F1ZV" maxLength="15"
                    value={formData.gstNumber} onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn denim-seller" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card denim-seller" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>{gstInfo.status}</span>
                    <span className="gst-verified denim-seller">GST Verified</span>
                  </div>
                  <div className="gst-info-row"><span className="gst-label denim-seller">Legal Name</span><span className="gst-value">{gstInfo.legal_name || '-'}</span></div>
                  {gstInfo.trade_name && <div className="gst-info-row"><span className="gst-label denim-seller">Trade Name</span><span className="gst-value">{gstInfo.trade_name}</span></div>}
                  <div className="gst-info-row"><span className="gst-label denim-seller">Type</span><span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span></div>
                  {gstInfo.address && <div className="gst-info-row"><span className="gst-label denim-seller">Address</span><span className="gst-value small">{gstInfo.address}</span></div>}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="company">Company / Mill Name <span className="req">*</span></label>
                <input type="text" id="company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="email">Email ID <span className="req">*</span></label>
                <input type="email" id="email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.city ? "error" : ""}`}>
                <label htmlFor="city">City / Location <span className="req">*</span></label>
                <input type="text" id="city" data-testid="city-input" placeholder="e.g. Ahmedabad, Surat, Ichalkaranji"
                  value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                {errors.city && <div className="field-error">{errors.city}</div>}
              </div>

              <button type="submit" className="submit-btn denim-seller" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Join as Seller"}</span>
                {!isSubmitting && <span className="btn-arrow">&#8594;</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free to list &middot; No commission on first order &middot; <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon denim-seller">&#10003;</div>
            <div className="success-title">Application Received!</div>
            <div className="success-sub">Our seller onboarding team will contact you within 48 hours to set up your storefront.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn denim-seller">Visit Locofast Marketplace &#8594;</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Active<br/>Buyers</div></div>
          <div className="stat"><div className="stat-val">100+</div><div className="stat-lbl">Seller<br/>Partners</div></div>
          <div className="stat"><div className="stat-val">10K+</div><div className="stat-lbl">Orders<br/>Monthly</div></div>
        </div>

        <section className="section">
          <div className="section-label denim-seller-text">Why Sell on Locofast</div>
          <div className="section-title">Grow your denim<br/>business.</div>
          <div className="vp-list">
            {[
              { title: "500+ Verified Buyers", desc: "Access a ready network of fashion brands, garment manufacturers, and export houses actively sourcing denim." },
              { title: "Zero Listing Fee", desc: "List your entire denim range for free. No setup costs, no monthly charges, no hidden fees." },
              { title: "Secure Payment Protection", desc: "Every transaction is protected with our Money Safety Guarantee. Get paid on time, every time." },
              { title: "Dedicated Account Manager", desc: "Get a dedicated Locofast representative to help you manage orders, pricing, and buyer relationships." },
              { title: "PAN India Logistics", desc: "Integrated shipping and logistics support to deliver your denim to buyers across India." },
              { title: "Business Intelligence", desc: "Real-time dashboards showing demand trends, buyer behavior, and pricing insights for your denim range." }
            ].map((vp, i) => (
              <div key={i} className="vp-card denim-seller-highlight">
                <div className="vp-num denim-seller">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label denim-seller-text">How It Works</div>
          <div className="section-title">Start selling in<br/>3 simple steps.</div>
          <div className="steps">
            {[
              { title: "Register & Get Verified", desc: "Submit your details and GST. Our team verifies your mill and onboards you within 48 hours." },
              { title: "List Your Denim Range", desc: "Upload your fabric catalog with specs, pricing, and MOQ. Our team helps you set up." },
              { title: "Start Receiving Orders", desc: "Buyers discover your products, place orders, and you ship with payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle denim-seller">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// India Generic Seller/Vendor Onboarding Page
const IndiaSellerPage = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", gstNumber: "",
    company: "", city: "", fabricCategories: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) window.fbq('track', 'Lead');
  }, [isSuccess]);

  const FABRIC_CATEGORIES = [
    "Denim Fabric", "Shirting Fabric", "Kurti Fabric", "T-shirt Fabric", "Bottom-weight Fabric"
  ];

  const validateGST = (gst) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      fabricCategories: prev.fabricCategories.includes(cat) ? [] : [cat]
    }));
  };

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV"); return; }
    setGstLoading(true); setGstError(""); setGstInfo(null);
    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        const d = response.data.data;
        setGstInfo(d);
        setFormData(prev => ({
          ...prev,
          company: d.legal_name || prev.company,
          city: d.city || prev.city
        }));
      } else { setGstError(response.data.error || "GST lookup failed"); }
    } catch { setGstError("Failed to fetch GST details. Please try again."); }
    finally { setGstLoading(false); }
  };

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Please enter your name.";
    if (!formData.phone.trim() || formData.phone.length !== 10) e.phone = "Please enter a valid 10-digit phone number.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Please enter a valid email.";
    if (!formData.gstNumber.trim()) e.gstNumber = "Please enter your GST number.";
    else if (!validateGST(formData.gstNumber.toUpperCase())) e.gstNumber = "Invalid GST format.";
    if (!formData.company.trim()) e.company = "Verify GST to auto-fill company.";
    if (!formData.city.trim()) e.city = "Please enter your city.";
    if (formData.fabricCategories.length === 0) e.fabricCategories = "Please select a fabric category.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: `+91${formData.phone}`,
        city: formData.city,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        fabric_type: formData.fabricCategories.join(", "),
        campaign: "india-seller"
      });
      setIsSuccess(true);
    } catch { alert("Something went wrong. Please try again."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="landing-page india-seller-page">
      <div className="fold india-seller-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-seller">SELLER ONBOARDING</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Sell Your Fabrics on<br/><em>India's Largest</em><br/>B2B Platform</h1>
          <p className="hero-sub">Get access to 500+ verified buyers. More business, better utilisation, 100% payment safety. Zero commission to list.</p>

          <div className="denim-chips">
            {["More Business", "Better Utilisation", "100% Payment Safety", "PAN India Buyers"].map((tag, i) => (
              <span key={i} className="denim-chip seller-chip">{tag}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Buyers actively sourcing fabrics on Locofast</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Register as a Seller</div>
            <div className="form-sub">Join India's top mills & manufacturers already selling on Locofast. Our team will onboard you within 48 hours.</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="sel-name">Contact Name <span className="req">*</span></label>
                <input type="text" id="sel-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="sel-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="sel-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="sel-email">Email ID <span className="req">*</span></label>
                <input type="email" id="sel-email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="sel-gst">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="sel-gst" data-testid="gst-input" placeholder="27AAPFU0939F1ZV" maxLength="15"
                    value={formData.gstNumber} onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn india-seller" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card india-seller" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>{gstInfo.status}</span>
                    <span className="gst-verified india-seller">GST Verified</span>
                  </div>
                  <div className="gst-info-row"><span className="gst-label india-seller">Legal Name</span><span className="gst-value">{gstInfo.legal_name || '-'}</span></div>
                  {gstInfo.trade_name && <div className="gst-info-row"><span className="gst-label india-seller">Trade Name</span><span className="gst-value">{gstInfo.trade_name}</span></div>}
                  <div className="gst-info-row"><span className="gst-label india-seller">Type</span><span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span></div>
                  {gstInfo.address && <div className="gst-info-row"><span className="gst-label india-seller">Address</span><span className="gst-value small">{gstInfo.address}</span></div>}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="sel-company">Company / Mill Name <span className="req">*</span></label>
                <input type="text" id="sel-company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.city ? "error" : ""}`}>
                <label htmlFor="sel-city">City / Location <span className="req">*</span></label>
                <input type="text" id="sel-city" data-testid="city-input" placeholder="e.g. Surat, Bhilwara, Ichalkaranji"
                  value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                {errors.city && <div className="field-error">{errors.city}</div>}
              </div>

              <div className={`field ${errors.fabricCategories ? "error" : ""}`}>
                <label>Fabric Categories You Supply <span className="req">*</span></label>
                <div className="seller-cat-grid" data-testid="fabric-category-selector">
                  {FABRIC_CATEGORIES.map((cat, i) => (
                    <button type="button" key={i} data-testid={`cat-btn-${i}`}
                      className={`seller-cat-btn ${formData.fabricCategories.includes(cat) ? "selected" : ""}`}
                      onClick={() => toggleCategory(cat)}>
                      {cat}
                    </button>
                  ))}
                </div>
                {errors.fabricCategories && <div className="field-error">{errors.fabricCategories}</div>}
              </div>

              <button type="submit" className="submit-btn india-seller" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Join as Seller"}</span>
                {!isSubmitting && <span className="btn-arrow">&#8594;</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free to list &middot; No commission on first order &middot; <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon india-seller">&#10003;</div>
            <div className="success-title">Application Received!</div>
            <div className="success-sub">Our seller onboarding team will contact you within 48 hours to set up your storefront.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn india-seller">Visit Locofast Marketplace &#8594;</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Active<br/>Buyers</div></div>
          <div className="stat"><div className="stat-val">100%</div><div className="stat-lbl">Payment<br/>Safety</div></div>
          <div className="stat"><div className="stat-val">PAN</div><div className="stat-lbl">India<br/>Coverage</div></div>
        </div>

        <section className="section">
          <div className="section-label india-seller-text">Why Sell on Locofast</div>
          <div className="section-title">Grow your fabric<br/>business.</div>
          <div className="vp-list">
            {[
              { title: "500+ Verified Buyers", desc: "Access a ready network of fashion brands, garment manufacturers, and export houses actively sourcing fabrics across all categories." },
              { title: "Better Capacity Utilisation", desc: "Stop running at 60% capacity. Fill idle looms with steady orders from brands across India. No more dependency on local traders." },
              { title: "100% Payment Safety", desc: "Every transaction on Locofast is protected. Our escrow-backed payment system ensures you get paid on time, every time. No defaults." },
              { title: "PAN India Buyers", desc: "Reach brands in Delhi, Mumbai, Bangalore, Kolkata and beyond. Your fabric quality, now accessible to the entire Indian market." },
              { title: "Zero Listing, Zero Commission", desc: "List your entire fabric range for free. No setup costs, no monthly charges, no commission on your first orders." },
              { title: "Dedicated Account Manager", desc: "Get a Locofast representative who understands your category. Help with pricing, sampling, order management and buyer relationships." }
            ].map((vp, i) => (
              <div key={i} className="vp-card india-seller-highlight">
                <div className="vp-num india-seller">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label india-seller-text">Fabric Categories</div>
          <div className="section-title">What buyers are<br/>sourcing now.</div>
          <div className="knit-categories-grid">
            {[
              { name: "Denim Fabric", spec: "Raw, Stretch, Selvedge, Coated", use: "Jeans, Jackets, Casual Wear" },
              { name: "Shirting Fabric", spec: "Poplin, Cambric, Oxford, Dobby", use: "Formal & Casual Shirts" },
              { name: "Kurti Fabric", spec: "Rayon, Cotton, Modal, Chanderi", use: "Kurtis, Ethnic Wear, Dresses" },
              { name: "T-shirt Fabric", spec: "Single Jersey, Pique, Interlock", use: "T-shirts, Polo Shirts, Active Wear" },
              { name: "Bottom-weight Fabric", spec: "Twill, Chino, Drill, Gabardine", use: "Trousers, Cargo, Chinos" }
            ].map((cat, i) => (
              <div key={i} className="knit-cat-card" data-testid={`seller-cat-card-${i}`}>
                <div className="knit-cat-name">{cat.name}</div>
                <div className="knit-cat-spec">{cat.spec}</div>
                <div className="knit-cat-use">{cat.use}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label india-seller-text">How It Works</div>
          <div className="section-title">Start selling in<br/>3 simple steps.</div>
          <div className="steps">
            {[
              { title: "Register & Get Verified", desc: "Submit your details and GST. Our team verifies your unit and onboards you within 48 hours." },
              { title: "List Your Fabric Range", desc: "Upload your catalog with fabric specs, pricing, and MOQ. Our team helps you optimize your listings." },
              { title: "Start Receiving Orders", desc: "Buyers discover your fabrics, place orders, and you ship with 100% payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle india-seller">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// India Shirt Seller Landing Page Component
const IndiaShirtSellerPage = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", gstNumber: "",
    company: "", city: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) window.fbq('track', 'Lead');
  }, [isSuccess]);

  const validateGST = (gst) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV"); return; }
    setGstLoading(true); setGstError(""); setGstInfo(null);
    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        const d = response.data.data;
        setGstInfo(d);
        setFormData(prev => ({
          ...prev,
          company: d.legal_name || prev.company,
          city: d.city || prev.city
        }));
      } else { setGstError(response.data.error || "GST lookup failed"); }
    } catch { setGstError("Failed to fetch GST details. Please try again."); }
    finally { setGstLoading(false); }
  };

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Please enter your name.";
    if (!formData.phone.trim() || formData.phone.length !== 10) e.phone = "Please enter a valid 10-digit phone number.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Please enter a valid email.";
    if (!formData.gstNumber.trim()) e.gstNumber = "Please enter your GST number.";
    else if (!validateGST(formData.gstNumber.toUpperCase())) e.gstNumber = "Invalid GST format.";
    if (!formData.company.trim()) e.company = "Verify GST to auto-fill company.";
    if (!formData.city.trim()) e.city = "Please enter your city.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: `+91${formData.phone}`,
        city: formData.city,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-shirt-seller"
      });
      setIsSuccess(true);
    } catch { alert("Something went wrong. Please try again."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="landing-page shirt-seller-page">
      <div className="fold shirt-seller-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-shirt-seller">SHIRT FABRICS</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Sell Your Shirting on<br/><em>India's Largest</em><br/>B2B Fabric Platform</h1>
          <p className="hero-sub">Get access to 500+ verified buyers — fashion brands, garment manufacturers & export houses. Zero commission to list.</p>

          <div className="denim-chips">
            {["More Business", "Better Utilisation", "100% Payment Safety", "PAN India Buyers"].map((tag, i) => (
              <span key={i} className="denim-chip shirt-seller-chip">{tag}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Buyers actively sourcing shirting on Locofast</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Register as a Shirt Fabric Seller</div>
            <div className="form-sub">Join India's top shirting mills already selling on Locofast. Our team will onboard you within 48 hours.</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="ss-name">Contact Name <span className="req">*</span></label>
                <input type="text" id="ss-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="ss-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="ss-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="ss-email">Email ID <span className="req">*</span></label>
                <input type="email" id="ss-email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="ss-gst">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="ss-gst" data-testid="gst-input" placeholder="27AAPFU0939F1ZV" maxLength="15"
                    value={formData.gstNumber} onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn shirt-seller" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card shirt-seller" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>{gstInfo.status}</span>
                    <span className="gst-verified shirt-seller">GST Verified</span>
                  </div>
                  <div className="gst-info-row"><span className="gst-label shirt-seller">Legal Name</span><span className="gst-value">{gstInfo.legal_name || '-'}</span></div>
                  {gstInfo.trade_name && <div className="gst-info-row"><span className="gst-label shirt-seller">Trade Name</span><span className="gst-value">{gstInfo.trade_name}</span></div>}
                  <div className="gst-info-row"><span className="gst-label shirt-seller">Type</span><span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span></div>
                  {gstInfo.address && <div className="gst-info-row"><span className="gst-label shirt-seller">Address</span><span className="gst-value small">{gstInfo.address}</span></div>}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="ss-company">Company / Mill Name <span className="req">*</span></label>
                <input type="text" id="ss-company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className={`field ${errors.city ? "error" : ""}`}>
                <label htmlFor="ss-city">City / Location <span className="req">*</span></label>
                <input type="text" id="ss-city" data-testid="city-input" placeholder="e.g. Ichalkaranji, Bhilwara, Surat"
                  value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                {errors.city && <div className="field-error">{errors.city}</div>}
                {gstInfo && gstInfo.city && !formData.city && <div className="field-hint">Suggested: {gstInfo.city}</div>}
              </div>

              <button type="submit" className="submit-btn shirt-seller" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Join as Seller"}</span>
                {!isSubmitting && <span className="btn-arrow">&#8594;</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free to list &middot; No commission on first order &middot; <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon shirt-seller">&#10003;</div>
            <div className="success-title">Application Received!</div>
            <div className="success-sub">Our seller onboarding team will contact you within 48 hours to set up your storefront.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn shirt-seller">Visit Locofast Marketplace &#8594;</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Active<br/>Buyers</div></div>
          <div className="stat"><div className="stat-val">100%</div><div className="stat-lbl">Payment<br/>Safety</div></div>
          <div className="stat"><div className="stat-val">PAN</div><div className="stat-lbl">India<br/>Coverage</div></div>
        </div>

        <section className="section">
          <div className="section-label shirt-seller-text">Shirting Categories in Demand</div>
          <div className="section-title">What buyers are<br/>sourcing now.</div>
          <div className="knit-categories-grid">
            {[
              { name: "Cotton Poplin", spec: "40s-60s | 100% Cotton", use: "Formal & Casual Shirts" },
              { name: "Cotton Cambric", spec: "60s-80s | Fine Weave", use: "Premium Shirts, Dresses" },
              { name: "Oxford Weave", spec: "40s-60s | Basket Weave", use: "Button-downs, Casual Shirts" },
              { name: "Twill Shirting", spec: "40s-60s | 2/1 & 3/1 Twill", use: "Formal Shirts, Workwear" },
              { name: "Linen & Linen Blends", spec: "Lea 40-60 | Natural Fibre", use: "Summer Shirts, Resort Wear" },
              { name: "Dobby & Jacquard", spec: "40s-80s | Self-textured", use: "Premium Formal Shirts" }
            ].map((cat, i) => (
              <div key={i} className="knit-cat-card" data-testid={`shirt-cat-${i}`}>
                <div className="knit-cat-name">{cat.name}</div>
                <div className="knit-cat-spec">{cat.spec}</div>
                <div className="knit-cat-use">{cat.use}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label shirt-seller-text">Why Sell Shirting on Locofast</div>
          <div className="section-title">Grow your shirting<br/>business.</div>
          <div className="vp-list">
            {[
              { title: "500+ Verified Buyers", desc: "Access a ready network of fashion brands, garment manufacturers, and export houses actively sourcing shirting fabrics." },
              { title: "Better Capacity Utilisation", desc: "Stop running looms at 60% capacity. Fill idle capacity with steady orders from brands across India." },
              { title: "100% Payment Safety", desc: "Every transaction is protected with our escrow-backed payment system. Get paid on time, every time. No defaults." },
              { title: "PAN India Reach", desc: "Reach buyers in Delhi, Mumbai, Bangalore, Kolkata and beyond. Your shirting quality, now accessible nationwide." },
              { title: "Zero Listing, Zero Commission", desc: "List your entire shirting range for free. No setup costs, no monthly charges, no commission on first orders." },
              { title: "Dedicated Account Manager", desc: "Get a Locofast representative who understands shirting. Help with pricing, sampling, and buyer relationships." }
            ].map((vp, i) => (
              <div key={i} className="vp-card shirt-seller-highlight">
                <div className="vp-num shirt-seller">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label shirt-seller-text">How It Works</div>
          <div className="section-title">Start selling in<br/>3 simple steps.</div>
          <div className="steps">
            {[
              { title: "Register & Get Verified", desc: "Submit your details and GST. Our team verifies your mill and onboards you within 48 hours." },
              { title: "List Your Shirting Range", desc: "Upload your fabric catalog with count, construction, width, and MOQ. Our team helps you set up." },
              { title: "Start Receiving Orders", desc: "Buyers discover your shirting, place orders, and you ship with 100% payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle shirt-seller">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// India Cotton Landing Page Component
const IndiaCottonLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "", company: "", email: "", phone: "", gstNumber: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) window.fbq('track', 'Lead');
  }, [isSuccess]);

  const cottonQualities = [
    { name: "60s Cotton Cambric", spec: "90 x 84 | 62\"", gsm: "70 GSM", use: "Shirts, Dresses", loom: "Airjet" },
    { name: "30s Cotton Slub", spec: "60 x 56 | 62\"", gsm: "95 GSM", use: "Kurtis, Casual Wear", loom: "Airjet" },
    { name: "40s Cotton Poplin", spec: "132 x 72 | 62\"", gsm: "125 GSM", use: "Shirts, Uniforms", loom: "Airjet" },
    { name: "40s Poplin Lycra", spec: "124 x 46 | 62\"", gsm: "165 GSM", use: "Stretch Shirts, Trousers", loom: "Airjet", comp: "97/3 Cotton-Spandex" },
    { name: "40s Double Cloth", spec: "100 x 76 | 62\"", gsm: "110 GSM", use: "Jackets, Outerwear", loom: "Airjet" },
    { name: "20s Cotton Sheeting", spec: "60 x 56 | 62\"", gsm: "250 GSM", use: "Workwear, Home Textiles", loom: "Sulzer" },
    { name: "20s Cotton Twill 3/1", spec: "108 x 56 | 62\"", gsm: "210 GSM", use: "Cargo, Workwear", loom: "Airjet" },
    { name: "40s Cotton Twill 2/1", spec: "132 x 70 | 62\"", gsm: "165 GSM", use: "Chinos, Trousers", loom: "Airjet" },
    { name: "30s Cotton Twill 2/1", spec: "124 x 54 | 62\"", gsm: "150 GSM", use: "Bottoms, Cargo Pants", loom: "Airjet" },
    { name: "30s Twill Peach Finish", spec: "132 x 70 | 58\"", gsm: "170 GSM", use: "Premium Trousers, Chinos", loom: "Airjet" },
  ];

  const validateGST = (gst) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 27AAPFU0939F1ZV"); return; }
    setGstLoading(true); setGstError(""); setGstInfo(null);
    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        setGstInfo(response.data.data);
        if (!formData.company && response.data.data.legal_name) {
          setFormData(prev => ({ ...prev, company: response.data.data.legal_name }));
        }
      } else { setGstError(response.data.error || "GST lookup failed"); }
    } catch { setGstError("Failed to fetch GST details. Please try again."); }
    finally { setGstLoading(false); }
  };

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Please enter your name.";
    if (!formData.company.trim()) e.company = "Please enter your company name.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Please enter a valid business email.";
    if (!formData.phone.trim() || formData.phone.length !== 10) e.phone = "Please enter a valid 10-digit number.";
    if (!formData.gstNumber.trim()) e.gstNumber = "Please enter your GST number.";
    else if (!validateGST(formData.gstNumber.toUpperCase())) e.gstNumber = "Invalid GST format.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: `+91${formData.phone}`,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-cotton"
      });
      setIsSuccess(true);
    } catch { alert("Something went wrong. Please try again."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="landing-page india-cotton">
      <div className="fold cotton-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-cotton">100% COTTON</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Cotton Fabric at<br/><em>Best Mill Prices</em></h1>
          <p className="hero-sub">Cambric, Poplin, Twill, Sheeting & more. Direct from verified mills. Cash or credit.</p>

          <div className="denim-chips">
            <span className="denim-chip cotton-chip">Cambric</span>
            <span className="denim-chip cotton-chip">Poplin</span>
            <span className="denim-chip cotton-chip">Twill</span>
            <span className="denim-chip cotton-chip">Sheeting</span>
            <span className="denim-chip cotton-chip">Slub</span>
          </div>
          <div className="denim-chips composition-chips">
            <span className="denim-chip comp">500+ Mills</span>
            <span className="denim-chip comp">Cash or Credit</span>
            <span className="denim-chip comp">48hr Delivery</span>
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Trusted by India's top cotton buyers & D2C brands</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Get Best Rates & Free Samples</div>
            <div className="form-sub">Share your cotton fabric requirements — our experts will reach out within 24 hours.</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="ct-name">Name <span className="req">*</span></label>
                <input type="text" id="ct-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="ct-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="ct-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="ct-email">Email ID <span className="req">*</span></label>
                <input type="email" id="ct-email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="ct-gst">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="ct-gst" data-testid="gst-input" placeholder="27AAPFU0939F1ZV" maxLength="15"
                    value={formData.gstNumber} onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn cotton" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card cotton" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>{gstInfo.status}</span>
                    <span className="gst-verified cotton">GST Verified</span>
                  </div>
                  <div className="gst-info-row"><span className="gst-label cotton">Legal Name</span><span className="gst-value">{gstInfo.legal_name || '-'}</span></div>
                  {gstInfo.trade_name && <div className="gst-info-row"><span className="gst-label cotton">Trade Name</span><span className="gst-value">{gstInfo.trade_name}</span></div>}
                  <div className="gst-info-row"><span className="gst-label cotton">Type</span><span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span></div>
                  {gstInfo.address && <div className="gst-info-row"><span className="gst-label cotton">Address</span><span className="gst-value small">{gstInfo.address}</span></div>}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="ct-company">Company Name <span className="req">*</span></label>
                <input type="text" id="ct-company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <button type="submit" className="submit-btn cotton" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Get Cotton Rates"}</span>
                {!isSubmitting && <span className="btn-arrow">&#8594;</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free samples available &middot; No commitment &middot; <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon cotton">&#10003;</div>
            <div className="success-title">Request Received!</div>
            <div className="success-sub">Our cotton sourcing team will share the best rates and samples within 24 hours.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn cotton">Browse Cotton Fabrics &#8594;</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Verified<br/>Mills</div></div>
          <div className="stat"><div className="stat-val">10K+</div><div className="stat-lbl">Fabric<br/>Varieties</div></div>
          <div className="stat"><div className="stat-val">48hr</div><div className="stat-lbl">Sample<br/>Delivery</div></div>
        </div>

        <section className="section">
          <div className="section-label cotton-text">Cotton Catalogue</div>
          <div className="section-title">Reference qualities<br/>available now.</div>
          <div className="cotton-catalogue">
            {cottonQualities.map((q, i) => (
              <div key={i} className="cotton-card" data-testid={`cotton-card-${i}`}>
                <div className="cotton-card-head">
                  <div className="cotton-card-name">{q.name}</div>
                  <div className="cotton-card-gsm">{q.gsm}</div>
                </div>
                <div className="cotton-card-spec">{q.spec}</div>
                {q.comp && <div className="cotton-card-comp">{q.comp}</div>}
                <div className="cotton-card-foot">
                  <span className="cotton-card-use">{q.use}</span>
                  <span className="cotton-card-loom">{q.loom}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="cotton-mto-banner" data-testid="mto-banner">
            <div className="cotton-mto-title">Made to Order</div>
            <div className="cotton-mto-desc">100% Cotton fabrics from 12s to 80s count. MOQ: 5,000m (&lt;30s) | 10,000m (&gt;30s)</div>
          </div>
        </section>

        <section className="section">
          <div className="section-label cotton-text">Why Locofast</div>
          <div className="section-title">Everything in<br/>one platform.</div>
          <div className="vp-list">
            {[
              { title: "500+ Verified Cotton Mills", desc: "Direct access to India's largest network of cotton mills — Cambric, Poplin, Twill, Sheeting, and specialty weaves." },
              { title: "Transparent Pricing", desc: "No hidden costs. Clear pricing per metre displayed for every quality. Compare across mills instantly." },
              { title: "Money Safety Guarantee", desc: "Locofast's secure payment system protects every transaction. Pay with confidence knowing your money is safeguarded." },
              { title: "48hr Sample Delivery", desc: "Get fabric samples delivered to your doorstep within 48 hours. Test quality before committing to bulk orders." },
              { title: "Made to Order", desc: "Need custom construction? Get fabrics made to your exact specs — count, reed, pick, width, and finish. 12s to 80s count." },
              { title: "Cash or Credit", desc: "Flexible payment options. Buy on credit or pay upfront with instant confirmation and faster dispatch." }
            ].map((vp, i) => (
              <div key={i} className="vp-card cotton-highlight">
                <div className="vp-num cotton">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label cotton-text">How It Works</div>
          <div className="section-title">Source cotton in<br/>3 simple steps.</div>
          <div className="steps">
            {[
              { title: "Share Your Requirement", desc: "Tell us the cotton quality, count, construction, and quantity you need. Our team gets to work immediately." },
              { title: "Get Matched & Compare", desc: "Receive quotes from multiple verified mills. Compare pricing, MOQ, and delivery timelines side by side." },
              { title: "Order with Confidence", desc: "Place your order with Money Safety Guarantee. Track delivery and get samples within 48 hours." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle cotton">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

// India Knit Seller Landing Page — Tirupur
const IndiaKnitSellerPage = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", gstNumber: "",
    company: "", city: "Tirupur"
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const lastGstCall = useRef(0);

  useEffect(() => {
    if (isSuccess && window.fbq) window.fbq('track', 'Lead');
  }, [isSuccess]);

  const validateGST = (gst) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);

  const lookupGST = async () => {
    const now = Date.now();
    if (now - lastGstCall.current < 3000) { setGstError("Please wait a moment before retrying."); return; }
    lastGstCall.current = now;
    if (!formData.gstNumber.trim()) { setGstError("Please enter a GST number"); return; }
    if (!validateGST(formData.gstNumber.toUpperCase())) { setGstError("Invalid GST format. Example: 33AABCT1332L1ZK"); return; }
    setGstLoading(true); setGstError(""); setGstInfo(null);
    try {
      const response = await axios.post(`${API}/gst/lookup`, { gstin: formData.gstNumber.toUpperCase() });
      if (response.data.success) {
        const d = response.data.data;
        setGstInfo(d);
        setFormData(prev => ({
          ...prev,
          company: d.legal_name || prev.company,
          city: d.city || prev.city
        }));
      } else { setGstError(response.data.error || "GST lookup failed"); }
    } catch { setGstError("Failed to fetch GST details. Please try again."); }
    finally { setGstLoading(false); }
  };

  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Please enter your name.";
    if (!formData.phone.trim() || formData.phone.length !== 10) e.phone = "Please enter a valid 10-digit phone number.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Please enter a valid email.";
    if (!formData.gstNumber.trim()) e.gstNumber = "Please enter your GST number.";
    else if (!validateGST(formData.gstNumber.toUpperCase())) e.gstNumber = "Invalid GST format.";
    if (!formData.company.trim()) e.company = "Verify GST to auto-fill company.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: `+91${formData.phone}`,
        city: formData.city,
        gst_number: formData.gstNumber.toUpperCase(),
        gst_info: gstInfo,
        campaign: "india-knits-seller-tirupur"
      });
      setIsSuccess(true);
    } catch { alert("Something went wrong. Please try again."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="landing-page knit-seller-page">
      <div className="fold knit-seller-gradient">
        <div className="blob-tr"></div>
        <div className="blob-bl"></div>

        <div className="hero-section">
          <div className="trust-pill" data-testid="trust-pill">
            <span className="trust-pill-tag-knit">TIRUPUR KNITS</span>
            <span>India's Largest B2B Fabric Platform</span>
          </div>
          <h1>Grow Your Knit Business<br/>with <em>PAN India Buyers</em></h1>
          <p className="hero-sub">Get access to 500+ verified buyers across India. 100% payment safety. Zero commission to list.</p>

          <div className="denim-chips">
            {["More Business", "Better Utilisation", "100% Payment Safety", "PAN India Buyers"].map((tag, i) => (
              <span key={i} className="denim-chip knit-chip">{tag}</span>
            ))}
          </div>
        </div>

        <div className="marquee-wrap">
          <div className="marquee-label">Buyers actively sourcing knits on Locofast</div>
          <div className="marquee-track">
            {[...BRAND_TICKER, ...BRAND_TICKER].map((name, i) => (
              <div key={i} className="m-chip">{name}</div>
            ))}
          </div>
        </div>

        {!isSuccess ? (
          <div className="form-card" data-testid="lead-form-card">
            <div className="form-head">Register as a Knit Seller</div>
            <div className="form-sub">Join Tirupur's top mills already selling on Locofast. Our team will onboard you within 48 hours.</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className={`field ${errors.name ? "error" : ""}`}>
                <label htmlFor="kn-name">Name <span className="req">*</span></label>
                <input type="text" id="kn-name" data-testid="name-input" placeholder="Your full name"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className={`field ${errors.phone ? "error" : ""}`}>
                <label htmlFor="kn-phone">Phone Number <span className="req">*</span></label>
                <div className="phone-wrap">
                  <div className="phone-prefix">+91</div>
                  <input type="tel" id="kn-phone" data-testid="phone-input" placeholder="9XXXXXXXXX" maxLength="10"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>

              <div className={`field ${errors.email ? "error" : ""}`}>
                <label htmlFor="kn-email">Email ID <span className="req">*</span></label>
                <input type="email" id="kn-email" data-testid="email-input" placeholder="you@company.com"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className={`field ${errors.gstNumber ? "error" : ""}`}>
                <label htmlFor="kn-gst">GST Number <span className="req">*</span></label>
                <div className="gst-input-wrap">
                  <input type="text" id="kn-gst" data-testid="gst-input" placeholder="33AABCT1332L1ZK" maxLength="15"
                    value={formData.gstNumber} onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })} />
                  <button type="button" className="gst-lookup-btn knit-seller" data-testid="gst-verify-btn" onClick={lookupGST} disabled={gstLoading || !!gstInfo}>
                    {gstLoading ? "..." : gstInfo ? "Verified" : "Verify"}
                  </button>
                </div>
                {errors.gstNumber && <div className="field-error">{errors.gstNumber}</div>}
                {gstError && <div className="field-error">{gstError}</div>}
              </div>

              {gstInfo && (
                <div className="gst-info-card knit-seller" data-testid="gst-info-card">
                  <div className="gst-info-header">
                    <span className={`gst-status ${gstInfo.status === 'Active' ? 'active' : 'inactive'}`}>{gstInfo.status}</span>
                    <span className="gst-verified knit-seller">GST Verified</span>
                  </div>
                  <div className="gst-info-row"><span className="gst-label knit-seller">Legal Name</span><span className="gst-value">{gstInfo.legal_name || '-'}</span></div>
                  {gstInfo.trade_name && <div className="gst-info-row"><span className="gst-label knit-seller">Trade Name</span><span className="gst-value">{gstInfo.trade_name}</span></div>}
                  <div className="gst-info-row"><span className="gst-label knit-seller">Type</span><span className="gst-value">{gstInfo.taxpayer_type || gstInfo.ctb || '-'}</span></div>
                  {gstInfo.address && <div className="gst-info-row"><span className="gst-label knit-seller">Address</span><span className="gst-value small">{gstInfo.address}</span></div>}
                </div>
              )}

              <div className={`field ${errors.company ? "error" : ""}`}>
                <label htmlFor="kn-company">Company Name <span className="req">*</span></label>
                <input type="text" id="kn-company" data-testid="company-input" placeholder="Verify GST to auto-fill"
                  value={formData.company} readOnly disabled className="auto-fill-field" />
                {errors.company && <div className="field-error">{errors.company}</div>}
                {!formData.company && <div className="field-hint">Company name will be auto-filled from GST verification</div>}
              </div>

              <div className="field">
                <label htmlFor="kn-city">City <span className="req">*</span></label>
                <input type="text" id="kn-city" data-testid="city-input" placeholder="Tirupur"
                  value={formData.city} readOnly disabled className="auto-fill-field" />
                <div className="field-hint">City auto-filled from GST verification</div>
              </div>

              <button type="submit" className="submit-btn knit-seller" data-testid="submit-btn" disabled={isSubmitting || !formData.company}>
                <span className="btn-text">{isSubmitting ? "Submitting..." : "Register as Seller"}</span>
                {!isSubmitting && <span className="btn-arrow">&#8594;</span>}
                {isSubmitting && <div className="spinner"></div>}
              </button>

              <p className="form-note">
                Free to list &middot; No commission &middot; <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer">Terms apply</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="success-card" data-testid="success-card">
            <div className="success-icon knit-seller">&#10003;</div>
            <div className="success-title">Application Received!</div>
            <div className="success-sub">Our seller onboarding team will contact you within 48 hours to get you started.</div>
            <a href="https://shop.locofast.com/" target="_blank" rel="noopener noreferrer" className="shop-btn knit-seller">Visit Locofast Marketplace &#8594;</a>
          </div>
        )}
      </div>

      <div className="content-area">
        <div className="stats-strip">
          <div className="stat"><div className="stat-val">500+</div><div className="stat-lbl">Active<br/>Buyers</div></div>
          <div className="stat"><div className="stat-val">100%</div><div className="stat-lbl">Payment<br/>Safety</div></div>
          <div className="stat"><div className="stat-val">PAN</div><div className="stat-lbl">India<br/>Coverage</div></div>
        </div>

        <section className="section">
          <div className="section-label knit-seller-text">Why Sell Knits on Locofast</div>
          <div className="section-title">Scale your knit<br/>business.</div>
          <div className="vp-list">
            {[
              { title: "More Business, Guaranteed", desc: "Get matched with 500+ verified buyers — fashion brands, garment manufacturers & export houses actively sourcing knits, hosiery & cotton fabrics." },
              { title: "Better Capacity Utilisation", desc: "Stop running at 60% capacity. Fill idle looms with steady orders from brands across India. No more dependency on local traders." },
              { title: "100% Payment Safety", desc: "Every transaction on Locofast is protected. Our escrow-backed payment system ensures you get paid on time, every time. No defaults." },
              { title: "PAN India Buyers", desc: "Reach brands in Delhi, Mumbai, Bangalore, Kolkata and beyond. Your Tirupur quality, now accessible to the entire Indian market." },
              { title: "Zero Listing, Zero Commission", desc: "List your entire knit range for free. No setup costs, no monthly charges, no commission on your first orders." },
              { title: "Dedicated Account Manager", desc: "Get a Locofast representative who understands knits. Help with pricing, sampling, order management and buyer relationships." }
            ].map((vp, i) => (
              <div key={i} className="vp-card knit-seller-highlight">
                <div className="vp-num knit-seller">{i + 1}</div>
                <div><div className="vp-title">{vp.title}</div><div className="vp-desc">{vp.desc}</div></div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label knit-seller-text">Knit Categories in Demand</div>
          <div className="section-title">What buyers are<br/>sourcing now.</div>
          <div className="knit-categories-grid">
            {[
              { name: "Single Jersey", spec: "30s-40s Combed Cotton", use: "T-shirts, Innerwear" },
              { name: "Interlock", spec: "40s-60s Cotton/PC Blend", use: "Polo Shirts, Kids Wear" },
              { name: "Pique Knit", spec: "20s-30s Cotton Pique", use: "Polo T-shirts, Sportswear" },
              { name: "Fleece / French Terry", spec: "20s-30s OE/Combed", use: "Sweatshirts, Hoodies" },
              { name: "Rib Knit", spec: "1x1 / 2x2 Rib Cotton", use: "Cuffs, Collars, Bodywear" },
              { name: "Hosiery / Lycra", spec: "Cotton Lycra / Spandex", use: "Leggings, Active Wear" }
            ].map((cat, i) => (
              <div key={i} className="knit-cat-card" data-testid={`knit-cat-${i}`}>
                <div className="knit-cat-name">{cat.name}</div>
                <div className="knit-cat-spec">{cat.spec}</div>
                <div className="knit-cat-use">{cat.use}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-label knit-seller-text">How It Works</div>
          <div className="section-title">Start selling in<br/>3 simple steps.</div>
          <div className="steps">
            {[
              { title: "Register & Get Verified", desc: "Submit your details and GST. Our team verifies your unit and onboards you within 48 hours." },
              { title: "List Your Knit Range", desc: "Upload your fabric catalog with GSM, composition, width, and MOQ. Our team helps you optimize." },
              { title: "Start Receiving Orders", desc: "Buyers discover your knits, place orders, and you ship with 100% payment protection." }
            ].map((step, i) => (
              <div key={i} className="step"><div className="step-circle knit-seller">{String(i + 1).padStart(2, "0")}</div><div><div className="step-title">{step.title}</div><div className="step-desc">{step.desc}</div></div></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ErrorBoundary><BangladeshLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/bangladesh/generic" element={<ErrorBoundary><BangladeshLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/bangladesh/shirt" element={<ErrorBoundary><BangladeshShirtLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/bangladesh/pants" element={<ErrorBoundary><BangladeshPantsLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/colombia/denim" element={<ErrorBoundary><ColombiaDenimLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/sportswear" element={<ErrorBoundary><IndiaSportswearLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/rayon" element={<ErrorBoundary><IndiaRayonLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/cotton" element={<ErrorBoundary><IndiaCottonLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/srilanka" element={<ErrorBoundary><SriLankaLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/vietnam" element={<ErrorBoundary><VietnamLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/credit" element={<ErrorBoundary><IndiaCreditLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/denim" element={<ErrorBoundary><DenimLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/shirt" element={<ErrorBoundary><IndiaShirtLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/denim-seller" element={<ErrorBoundary><DenimSellerLandingPage /></ErrorBoundary>} />
            <Route path="/campaign/india/shirt-seller" element={<ErrorBoundary><IndiaShirtSellerPage /></ErrorBoundary>} />
            <Route path="/campaign/india/seller" element={<ErrorBoundary><IndiaSellerPage /></ErrorBoundary>} />
            <Route path="/campaign/india/seller/knits/tirupur" element={<ErrorBoundary><IndiaKnitSellerPage /></ErrorBoundary>} />
            <Route path="/campaign/admin" element={<ErrorBoundary><AdminPanel /></ErrorBoundary>} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
